import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useInviteAuth } from './InviteAuthContext';
import { useInviteTheme } from './InviteThemeContext';
import API_BASE_URL, { GOOGLE_CLIENT_ID } from '../../config';
import { Check, Globe, Calendar, Clock, RefreshCw, AlertCircle, CheckCircle2, MessageSquare, ExternalLink, ShieldCheck, ArrowRight } from 'lucide-react';
import { InviteCardGenerator } from '../../components/InviteCardGenerator';
import { AiInvitationStudio } from '../../components/AiInvitationStudio';
import { SUPPORTED_LANGUAGES } from './inviteLanguages';
import './InviteWizard.css';

interface Recipient {
    name: string;
    phone: string;
    email?: string;
    telegramChatId?: string;
    customFields?: {
        salutation?: string;
        relationship?: string;
        familySide?: string;
    };
}

// 15-minute time intervals for standard event time selection
const TIME_INTERVALS: Array<{ value: string; label: string; group: 'morning' | 'afternoon' | 'evening' | 'night' }> = (() => {
    const intervals: Array<{ value: string; label: string; group: 'morning' | 'afternoon' | 'evening' | 'night' }> = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
            const hStr = h < 10 ? `0${h}` : `${h}`;
            const mStr = m < 10 ? `0${m}` : `${m}`;
            const val = `${hStr}:${mStr}`;
            const period = h >= 12 ? 'PM' : 'AM';
            const displayH = h % 12 === 0 ? 12 : h % 12;
            const extra = val === '12:00' ? ' (Noon)' : val === '00:00' ? ' (Midnight)' : '';
            const label = `${displayH}:${mStr} ${period}${extra}`;

            let group: 'morning' | 'afternoon' | 'evening' | 'night' = 'night';
            if (h >= 6 && h < 12) group = 'morning';
            else if (h >= 12 && h < 17) group = 'afternoon';
            else if (h >= 17 && h < 24) group = 'evening';

            intervals.push({ value: val, label, group });
        }
    }
    return intervals;
})();

const formatHumanDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
        const parts = dateStr.split('-').map(Number);
        if (parts.length !== 3 || parts.some(isNaN)) return '';
        const dt = new Date(parts[0], parts[1] - 1, parts[2]);
        if (isNaN(dt.getTime())) return '';
        return dt.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return '';
    }
};

const formatHumanTime = (timeStr: string): string => {
    if (!timeStr) return '';
    try {
        const [hStr, mStr] = timeStr.split(':');
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        if (isNaN(h) || isNaN(m)) return '';
        const period = h >= 12 ? 'PM' : 'AM';
        const displayH = h % 12 === 0 ? 12 : h % 12;
        const displayM = m < 10 ? `0${m}` : `${m}`;
        return `${displayH}:${displayM} ${period}`;
    } catch {
        return '';
    }
};

const DEFAULT_FALLBACK_TEMPLATES = [
    {
        title: '💖 Warm & Traditional Invitation',
        content: 'Dearest {{name}},\n\nWith hearts full of love, we invite you to share in our joy as we celebrate {{eventName}}.\n\n📅 Date: {{date}}\n📍 Venue: {{venue}}, {{city}}\n\nYour presence and blessings mean the world to us. Please RSVP at: {{rsvpLink}}\n\nWarm regards,\nHost Family',
    },
    {
        title: '👑 Elegant & Formal Invitation',
        content: 'Dear {{name}},\n\nWe request the honor of your gracious presence to celebrate {{eventName}}.\n\n📅 Date: {{date}}\n📍 Venue: {{venue}}, {{city}}\n\nPlease confirm your attendance via the RSVP link below:\n{{rsvpLink}}\n\nSincerely,\nHost Family',
    },
    {
        title: '🎉 Joyful & Celebratory Invite',
        content: 'Hey {{name}}!\n\nWe are excited to invite you to celebrate {{eventName}} with us! Let\'s make wonderful memories together.\n\n📅 When: {{date}}\n📍 Where: {{venue}}, {{city}}\n\nKindly confirm your presence here: {{rsvpLink}}\n\nCheers,\nHost Family',
    },
    {
        title: '✨ Poetic Milestone Celebration',
        content: 'Dearest {{name}},\n\nA journey of love, joy, and togetherness unfolds. We warmly welcome you and your family to grace {{eventName}} and bless our celebration.\n\n📅 Date: {{date}}\n📍 Venue: {{venue}}, {{city}}\n\nRSVP link: {{rsvpLink}}\n\nWith love & gratitude,\nFamily',
    }
];

export const READY_MADE_CARD_THEMES = [
    { id: 'warm', name: 'Royal Gold', icon: '👑', color: '#D97706', bg: 'linear-gradient(135deg, #78350F 0%, #1E1B4B 100%)' },
    { id: 'poetic', name: 'Floral Elegance', icon: '🌸', color: '#EC4899', bg: 'linear-gradient(135deg, #831843 0%, #311042 100%)' },
    { id: 'casual', name: 'Modern Minimal', icon: '🌿', color: '#6366F1', bg: 'linear-gradient(135deg, #1E1B4B 0%, #0F172A 100%)' },
    { id: 'formal', name: 'Classic Maroon', icon: '🍷', color: '#DC2626', bg: 'linear-gradient(135deg, #450A0A 0%, #1C1917 100%)' },
    { id: 'humorous', name: 'Sunset Glow', icon: '🌅', color: '#EA580C', bg: 'linear-gradient(135deg, #7C2D12 0%, #312E81 100%)' },
];

export const InviteWizard: React.FC = () => {
    const { inviteToken, inviteUser, inviteGoogleLogin, inviteLogin, inviteRegister, refreshProfile } = useInviteAuth();
    const { theme } = useInviteTheme();
    const isLightTheme = theme === 'light';
    const history = useHistory();
    const location = useLocation();
    const isUserPremium = inviteUser ? inviteUser.isPremium : false;

    const query = new URLSearchParams(location.search);
    const editCampaignId = query.get('edit');

    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    // WhatsApp Guidance & Status State
    const [whatsappStatus, setWhatsappStatus] = useState<string>(() => inviteUser?.whatsappStatus || 'disconnected');
    const [whatsappPhone, setWhatsappPhone] = useState<string>(() => inviteUser?.whatsappPhoneNumber || '');
    const [checkingWaStatus, setCheckingWaStatus] = useState<boolean>(false);
    const [showWaStep1Modal, setShowWaStep1Modal] = useState<boolean>(false);
    const [showWaDispatchModal, setShowWaDispatchModal] = useState<boolean>(false);

    // Step 1: Event Config
    const [eventType, setEventType] = useState('');
    const [eventName, setEventName] = useState('');
    const [hostName, setHostName] = useState('');
    const [collectDietary, setCollectDietary] = useState(false);
    const [collectAccommodation, setCollectAccommodation] = useState(false);
    const [eventDate, setEventDate] = useState('');
    const [eventDatePart, setEventDatePart] = useState('');
    const [eventTimePart, setEventTimePart] = useState('');
    const [isCustomTime, setIsCustomTime] = useState(false);
    const dateInputRef = React.useRef<HTMLInputElement | null>(null);
    const timeSelectRef = React.useRef<HTMLSelectElement | null>(null);
    const timeInputRef = React.useRef<HTMLInputElement | null>(null);
    const [selectedCardTheme, setSelectedCardTheme] = useState<string>('warm');

    const todayStr = new Date().toISOString().split('T')[0];
    const maxAllowedYear = new Date().getFullYear() + 10;
    const maxDateStr = `${maxAllowedYear}-12-31`;

    const updateDateTime = (newDate: string, newTime: string) => {
        setEventDatePart(newDate);
        setEventTimePart(newTime);
        if (newDate && newTime) {
            setEventDate(`${newDate}T${newTime}`);
        } else if (newDate) {
            setEventDate(`${newDate}T00:00`);
        } else {
            setEventDate('');
        }
    };
    const [eventVenue, setEventVenue] = useState('');
    const [eventCity, setEventCity] = useState('');
    const [customEventType, setCustomEventType] = useState('');
    const [tone, setTone] = useState<'formal' | 'warm' | 'casual' | 'humorous' | 'poetic'>('warm');
    const [language, setLanguage] = useState('en');

    // Step 2: Message Content
    const [messageTemplate, setMessageTemplate] = useState('');
    const [suggestions, setSuggestions] = useState<Array<{ title: string; content: string }>>([]);
    const [fetchingSuggestions, setFetchingSuggestions] = useState(false);

    // Step 3: Recipients (Stored locally in state)
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [manualName, setManualName] = useState('');
    const [manualPhone, setManualPhone] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const [manualTelegram, setManualTelegram] = useState('');
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [importLogs, setImportLogs] = useState<string | null>(null);

    // Floating toast notification state (top-right corner)
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const toastTimerRef = React.useRef<NodeJS.Timeout | null>(null);

    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success', duration = 2000) => {
        if (toastTimerRef.current) {
            clearTimeout(toastTimerRef.current);
        }
        setToast({ message, type });
        toastTimerRef.current = setTimeout(() => {
            setToast(null);
        }, duration);
    };

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearTimeout(toastTimerRef.current);
            }
        };
    }, []);

    // Inline row editing state
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<Recipient>({ name: '', phone: '', email: '', telegramChatId: '' });
    const [editErrors, setEditErrors] = useState<Record<string, string>>({});

    // Step 4: Channels
    const [channels, setChannels] = useState<string[]>(['whatsapp']);
    const [notificationChannel, setNotificationChannel] = useState<'none' | 'whatsapp' | 'sms' | 'telegram' | 'email'>('none');

    // Step 5: Test Send & Auth Overlay
    const [testPhone, setTestPhone] = useState('');
    const [testResult, setTestResult] = useState<string | null>(null);
    
    // Auth Form State inside Wizard
    const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authName, setAuthName] = useState('');
    const [authError, setAuthError] = useState<string | null>(null);

    // Validation State
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
    const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
    const [templateType, setTemplateType] = useState<'text' | 'video'>('text');
    const [step2Tab, setStep2Tab] = useState<'library' | 'genai'>('library');
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

    // Media Attachment & 12-Hour Reminder State (₹699 & ₹999 plans)
    const [mediaUrl, setMediaUrl] = useState<string>('');
    const [mediaType, setMediaType] = useState<'image' | 'video' | 'none'>('none');
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [reminderEnabled, setReminderEnabled] = useState(false);

    const handleMediaUpload = async (file: File) => {
        const isEligiblePremium = inviteUser?.isPremium && (inviteUser.maxInvitations || 0) >= 1000;
        const totalMediaSent = inviteUser?.totalMediaInvitesSent || 0;
        const remainingMedia = Math.max(0, 10 - totalMediaSent);

        // If not on 1000+ plan, check if user has already exceeded the 10-media-invitation limit
        if (!isEligiblePremium) {
            if (totalMediaSent >= 10) {
                if (window.confirm('📸 Photo & Video Sending is included for up to 10 invitations across all plans. You have already used all 10 media invitations. Would you like to view packages and upgrade now?')) {
                    history.push('/invite/upgrade');
                }
                return;
            }

            if (recipients.length > remainingMedia) {
                const proceed = window.confirm(`📸 Photo & Video Sending is included for up to 10 guests across all plans (you have ${remainingMedia} media invitations remaining on your plan). You currently have ${recipients.length} guests in this campaign. Would you like to upgrade to the 1000 (₹699) or 2000 (₹999) plan to send to all guests? Click OK to upgrade, or Cancel to continue attaching media.`);
                if (proceed) {
                    history.push('/invite/upgrade');
                    return;
                }
            }
        }

        if (!inviteToken) {
            // Allow preview for anonymous free trial (up to 10 guests)
            const localUrl = URL.createObjectURL(file);
            setMediaUrl(localUrl);
            setMediaType(file.type.startsWith('video') ? 'video' : 'image');
            showToast('Photo template attached! Free trial includes up to 10 guests.', 'success', 3000);
            return;
        }

        setIsUploadingMedia(true);
        try {
            const formData = new FormData();
            formData.append('invitationMedia', file);

            const res = await fetch(`${API_BASE_URL}/api/invite/media/upload`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${inviteToken}`,
                },
                body: formData,
            });

            const data = await res.json();
            if (res.ok && data.success) {
                setMediaUrl(data.mediaUrl);
                setMediaType(data.mediaType);
                showToast('Media template attached successfully! (Included up to 10 invitations across all plans)', 'success', 2500);
            } else {
                alert(data.message || 'Failed to upload media');
            }
        } catch (err: any) {
            alert('Media upload failed: ' + err.message);
        } finally {
            setIsUploadingMedia(false);
        }
    };

    const [showRestoreBanner, setShowRestoreBanner] = useState(false);
    const [savedProgress, setSavedProgress] = useState<any>(null);

    // Function to save wizard progress locally
    const saveProgressToLocal = (step: number) => {
        const progress = {
            editCampaignId,
            currentStep: step,
            eventType,
            eventName,
            hostName,
            collectDietary,
            collectAccommodation,
            eventDate,
            eventVenue,
            eventCity,
            customEventType,
            tone,
            language,
            messageTemplate,
            recipients,
            channels,
            notificationChannel,
            templateType,
            mediaUrl,
            mediaType,
            reminderEnabled,
            timestamp: Date.now()
        };
        localStorage.setItem('shadigram_invite_wizard_draft', JSON.stringify(progress));
    };

    // Auto-save on any change to the form state
    useEffect(() => {
        if (!loading && (eventName || hostName || messageTemplate || recipients.length > 0)) {
            saveProgressToLocal(currentStep);
        }
    }, [currentStep, eventType, eventName, hostName, collectDietary, collectAccommodation, eventDate, eventVenue, eventCity, customEventType, tone, language, messageTemplate, recipients, channels, notificationChannel, templateType, mediaUrl, mediaType, reminderEnabled, loading, editCampaignId]);

    // Check for unsaved local draft on mount
    useEffect(() => {
        const localDraft = localStorage.getItem('shadigram_invite_wizard_draft');
        if (localDraft) {
            try {
                const parsed = JSON.parse(localDraft);
                const isRecent = Date.now() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000;
                // Only show restore banner if the draft's editCampaignId matches the query's editCampaignId
                const idMatches = (!editCampaignId && !parsed.editCampaignId) || (editCampaignId && parsed.editCampaignId === editCampaignId);
                
                if (isRecent && idMatches && (parsed.eventName || parsed.messageTemplate || parsed.recipients?.length > 0)) {
                    setSavedProgress(parsed);
                    setShowRestoreBanner(true);
                }
            } catch (e) {
                console.error('Error parsing local draft:', e);
            }
        }
    }, [editCampaignId]);

    const applySavedDraft = (draft: any, silent = false) => {
        if (!draft) return;
        setEventType(draft.eventType || '');
        setEventName(draft.eventName || '');
        setHostName(draft.hostName || '');
        setCollectDietary(Boolean(draft.collectDietary));
        setCollectAccommodation(Boolean(draft.collectAccommodation));
        if (draft.eventDate) {
            setEventDate(draft.eventDate);
            const [dPart, tPart] = draft.eventDate.split('T');
            setEventDatePart(dPart || '');
            setEventTimePart(tPart || '');
            if (tPart && !TIME_INTERVALS.some(t => t.value === tPart)) {
                setIsCustomTime(true);
            }
        } else {
            setEventDate('');
            setEventDatePart('');
            setEventTimePart('');
        }
        setEventVenue(draft.eventVenue || '');
        setEventCity(draft.eventCity || '');
        setCustomEventType(draft.customEventType || '');
        setTone(draft.tone || 'warm');
        setLanguage(draft.language || 'en');
        setMessageTemplate(draft.messageTemplate || '');
        setRecipients(draft.recipients || []);
        setChannels(draft.channels || ['whatsapp']);
        setNotificationChannel(draft.notificationChannel || 'none');
        setTemplateType(draft.templateType || 'text');
        setMediaUrl(draft.mediaUrl || '');
        setMediaType(draft.mediaType || 'none');
        setReminderEnabled(Boolean(draft.reminderEnabled));
        if (draft.currentStep) {
            setCurrentStep(draft.currentStep);
        }
        if (draft.editCampaignId) {
            history.replace(`/invite/new?edit=${draft.editCampaignId}`);
        }
        setShowRestoreBanner(false);
        if (!silent) {
            showToast('⚡ Unsaved progress restored successfully!', 'success', 3000);
        }
    };

    const handleRestoreProgress = () => {
        if (savedProgress) {
            applySavedDraft(savedProgress);
        }
    };

    const handleDismissProgress = () => {
        localStorage.removeItem('shadigram_invite_wizard_draft');
        setShowRestoreBanner(false);
    };

    // ─── WhatsApp Sync & Status Polling ──────────────────────────────────────
    useEffect(() => {
        if (inviteUser?.whatsappStatus) {
            setWhatsappStatus(inviteUser.whatsappStatus);
        }
        if (inviteUser?.whatsappPhoneNumber) {
            setWhatsappPhone(inviteUser.whatsappPhoneNumber);
        }
    }, [inviteUser]);

    const checkWhatsAppStatus = async (showToastFeedback = false) => {
        if (!inviteToken) return;
        setCheckingWaStatus(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/status`, {
                headers: { Authorization: `Bearer ${inviteToken}` },
            });
            if (res.ok) {
                const data = await res.json();
                const isConn = data.status === 'connected';
                setWhatsappStatus(data.status || 'disconnected');
                if (data.phoneNumber) {
                    setWhatsappPhone(data.phoneNumber);
                }
                if (showToastFeedback) {
                    if (isConn) {
                        showToast(`✅ WhatsApp Connected! Ready to broadcast.`, 'success', 3500);
                    } else {
                        showToast('WhatsApp is not connected yet. Please link your account.', 'info', 3500);
                    }
                }
            }
            if (refreshProfile) {
                await refreshProfile();
            }
        } catch (e) {
            console.error('Error checking WhatsApp status:', e);
            if (showToastFeedback) {
                showToast('Unable to check WhatsApp status.', 'error', 3000);
            }
        } finally {
            setCheckingWaStatus(false);
        }
    };

    // Initial check on mount
    useEffect(() => {
        if (inviteToken) {
            checkWhatsAppStatus(false);
        }
    }, [inviteToken]);

    // Handle return from WhatsApp settings
    useEffect(() => {
        const isFromWa = query.get('returnFrom') === 'whatsapp';
        const requestedStep = query.get('step');
        if (isFromWa) {
            const localDraft = localStorage.getItem('shadigram_invite_wizard_draft');
            if (localDraft) {
                try {
                    const parsed = JSON.parse(localDraft);
                    if (requestedStep) {
                        parsed.currentStep = Number(requestedStep);
                    }
                    applySavedDraft(parsed, true);
                    showToast('🎉 Welcome back! WhatsApp status updated and draft restored.', 'success', 4000);
                } catch (e) {
                    console.error('Error restoring draft after WhatsApp return:', e);
                }
            }
            if (inviteToken) {
                checkWhatsAppStatus(false);
            }
            // Clean returnFrom from URL while keeping editCampaignId if present
            const cleanUrl = editCampaignId ? `/invite/new?edit=${editCampaignId}` : '/invite/new';
            history.replace(cleanUrl);
        }
    }, []);

    // Quick action: navigate to connect WhatsApp with draft preservation
    const handleConnectWhatsApp = (targetStep?: number) => {
        const stepToSave = targetStep || currentStep;
        saveProgressToLocal(stepToSave);
        history.push(`/invite/whatsapp/settings?returnTo=/invite/new&step=${stepToSave}`);
    };

    // Load campaign details if editing an existing campaign (draft)
    useEffect(() => {
        const loadCampaignForEditing = async () => {
            if (!editCampaignId || !inviteToken) return;
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/${editCampaignId}`, {
                    headers: {
                        Authorization: `Bearer ${inviteToken}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    const camp = data.campaign;
                    setEventType(camp.eventType);
                    setEventName(camp.eventName);
                    setHostName(camp.hostName || '');
                    setCollectDietary(Boolean(camp.collectDietary));
                    setCollectAccommodation(Boolean(camp.collectAccommodation));
                    if (camp.eventDate) {
                        const d = new Date(camp.eventDate);
                        if (!isNaN(d.getTime())) {
                            const year = d.getFullYear();
                            const month = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            const hours = String(d.getHours()).padStart(2, '0');
                            const minutes = String(d.getMinutes()).padStart(2, '0');
                            const dPart = `${year}-${month}-${day}`;
                            const tPart = `${hours}:${minutes}`;
                            setEventDatePart(dPart);
                            setEventTimePart(tPart);
                            setEventDate(`${dPart}T${tPart}`);
                            if (tPart && !TIME_INTERVALS.some(t => t.value === tPart)) {
                                setIsCustomTime(true);
                            }
                        }
                    }
                    setEventVenue(camp.eventVenue);
                    setEventCity(camp.eventCity);
                    setTone(camp.tone || 'formal');
                    setLanguage(camp.language || 'en');
                    setMessageTemplate(camp.messageTemplate || '');
                    setChannels(camp.channels || ['whatsapp']);
                    setNotificationChannel(camp.notificationChannel || 'none');
                    setMediaUrl(camp.mediaUrl || '');
                    setMediaType(camp.mediaType || 'none');
                    setReminderEnabled(Boolean(camp.reminderEnabled));
                    setRecipients(data.recipients || []);
                }
            } catch (err: any) {
                console.error('Error loading campaign:', err.message);
            } finally {
                setLoading(false);
            }
        };
        loadCampaignForEditing();
    }, [editCampaignId, inviteToken]);

    // Live validation triggers for Step 1
    const validateStep1 = (currentTouched = touched, dPart = eventDatePart, tPart = eventTimePart) => {
        const errs: Record<string, string> = {};
        if (currentTouched.eventType && !eventType) {
            errs.eventType = 'Please select an event type.';
        }
        if (currentTouched.eventName && !eventName.trim()) {
            errs.eventName = 'Event Title Name is required.';
        }
        if (currentTouched.hostName && !hostName.trim()) {
            errs.hostName = 'Host Family / Hosts is required.';
        }
        
        const currentYear = new Date().getFullYear();
        if (currentTouched.eventDate) {
            if (!dPart) {
                errs.eventDate = 'Event Date is required.';
            } else {
                const parts = dPart.split('-').map(Number);
                const year = parts[0];
                if (isNaN(year) || parts.length !== 3) {
                    errs.eventDate = 'Please select a valid date.';
                } else if (year < currentYear) {
                    errs.eventDate = 'Event Date cannot be in the past.';
                } else if (year > maxAllowedYear) {
                    errs.eventDate = `Event Year cannot be beyond ${maxAllowedYear}.`;
                } else {
                    const selectedD = new Date(parts[0], parts[1] - 1, parts[2]);
                    const todayD = new Date();
                    todayD.setHours(0, 0, 0, 0);
                    if (selectedD < todayD) {
                        errs.eventDate = 'Event Date cannot be in the past.';
                    }
                }
            }
        }

        if (currentTouched.eventTime) {
            if (!tPart) {
                errs.eventTime = 'Event Time is required.';
            } else if (dPart && !errs.eventDate) {
                const parts = dPart.split('-').map(Number);
                const [hStr, mStr] = tPart.split(':');
                const combinedDate = new Date(parts[0], parts[1] - 1, parts[2], parseInt(hStr, 10), parseInt(mStr, 10));
                if (isNaN(combinedDate.getTime())) {
                    errs.eventTime = 'Please select a valid time.';
                } else if (combinedDate < new Date()) {
                    errs.eventTime = 'Selected time has already passed today. Please choose a future time.';
                }
            }
        }

        if (currentTouched.eventVenue && !eventVenue.trim()) {
            errs.eventVenue = 'Venue Name is required.';
        }
        if (currentTouched.eventCity && !eventCity.trim()) {
            errs.eventCity = 'City Name is required.';
        }
        if (eventType === 'custom' && currentTouched.customEventType && !customEventType.trim()) {
            errs.customEventType = 'Custom Event Type Name is required.';
        }
        setErrors(errs);

        const isDateValid = !!dPart && !errs.eventDate;
        const isTimeValid = !!tPart && !errs.eventTime;
        let isDateTimeFuture = false;
        if (isDateValid && isTimeValid) {
            const parts = dPart.split('-').map(Number);
            const [hStr, mStr] = tPart.split(':');
            const combined = new Date(parts[0], parts[1] - 1, parts[2], parseInt(hStr, 10), parseInt(mStr, 10));
            isDateTimeFuture = !isNaN(combined.getTime()) && combined >= new Date();
        }

        return Object.keys(errs).length === 0 && 
               !!eventType &&
               !!eventName.trim() && 
               !!hostName.trim() &&
               isDateValid &&
               isTimeValid &&
               isDateTimeFuture &&
               !!eventVenue.trim() && 
               !!eventCity.trim() &&
               (eventType !== 'custom' || !!customEventType.trim());
    };

    // Live validation triggers for Step 2
    const validateStep2 = () => {
        const errs: Record<string, string> = {};
        if (!messageTemplate.trim()) {
            errs.messageTemplate = 'Invitation Message cannot be empty.';
        } else {
            const hasRsvpLink = messageTemplate.includes('{{rsvpLink}}');
            if (!hasRsvpLink) {
                errs.messageTemplateWarning = '💡 Tip: Add the {{rsvpLink}} token so guests can click to RSVP.';
            }
        }
        setErrors(errs);
        return !errs.messageTemplate;
    };

    // ── Shared validation helpers ──────────────────────────────────────────
    // Phone: must be 10–15 digits (with optional leading + for country code)
    // Valid:   9876543210 (10-digit Indian), +919876543210 (12-digit with code)
    // Invalid: 9898 (4 digits), 989812 (6 digits), 98981213 (8 digits)
    const isValidPhone = (raw: string): boolean => {
        const sanitized = raw.replace(/[\s\-().]/g, '');
        return /^\+?[1-9]\d{9,14}$/.test(sanitized);
    };

    // Email: standard format check (optional field)
    const isValidEmail = (email: string): boolean =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Reset manual guest modal state
    const resetManualGuestForm = () => {
        setManualName('');
        setManualPhone('');
        setManualEmail('');
        setManualTelegram('');
        setErrors(prev => {
            const next = { ...prev };
            delete next.manualName;
            delete next.manualPhone;
            delete next.manualEmail;
            return next;
        });
        setTouched(prev => {
            const next = { ...prev };
            delete next.manualName;
            delete next.manualPhone;
            delete next.manualEmail;
            return next;
        });
    };

    // Manual guest form validation
    const validateManualGuest = (
        currentTouched = touched,
        nameVal = manualName,
        phoneVal = manualPhone,
        emailVal = manualEmail
    ) => {
        const errs: Record<string, string> = {};
        const trimmedName = nameVal.trim();
        const trimmedPhone = phoneVal.trim();
        const sanitizedPhone = trimmedPhone.replace(/[\s\-().]/g, '');

        if (currentTouched.manualName && !trimmedName) {
            errs.manualName = 'Guest Name is required.';
        }
        if (currentTouched.manualPhone) {
            if (!trimmedPhone) {
                errs.manualPhone = 'Phone Number is required.';
            } else if (!isValidPhone(trimmedPhone)) {
                errs.manualPhone = 'Enter a valid phone number with at least 10 digits (e.g., 9876543210 or +919876543210).';
            } else if (recipients.some(r => r.phone.replace(/[\s\-().]/g, '') === sanitizedPhone)) {
                errs.manualPhone = 'This phone number is already in your guest list.';
            }
        }
        if (emailVal && currentTouched.manualEmail) {
            if (!isValidEmail(emailVal.trim())) {
                errs.manualEmail = 'Please enter a valid email address (e.g., name@example.com).';
            }
        }

        setErrors(prev => {
            const next = { ...prev };
            if (errs.manualName) next.manualName = errs.manualName; else delete next.manualName;
            if (errs.manualPhone) next.manualPhone = errs.manualPhone; else delete next.manualPhone;
            if (errs.manualEmail) next.manualEmail = errs.manualEmail; else delete next.manualEmail;
            return next;
        });

        const isDuplicate = recipients.some(r => r.phone.replace(/[\s\-().]/g, '') === sanitizedPhone);

        return (
            !errs.manualName &&
            !errs.manualPhone &&
            !errs.manualEmail &&
            !!trimmedName &&
            isValidPhone(trimmedPhone) &&
            !isDuplicate &&
            (!emailVal.trim() || isValidEmail(emailVal.trim()))
        );
    };

    const handleNextStep1 = () => {
        // 1. Upload Validation: If an Excel/CSV file was selected but not uploaded yet
        if (excelFile) {
            showToast('Please click "Upload Contact List" to process your selected spreadsheet before proceeding.', 'error', 3500);
            return;
        }

        // 2. Upload Validation: If no guests added yet
        if (recipients.length === 0) {
            showToast('Please upload your contact list or add at least one guest before proceeding.', 'error', 3500);
            return;
        }

        // 3. Date Validation: Show toast & auto-focus date field if invalid
        const currentYear = new Date().getFullYear();
        let dateErrorMsg = '';
        if (!eventDatePart) {
            dateErrorMsg = 'Event Date is required. Please select an event date.';
        } else {
            const parts = eventDatePart.split('-').map(Number);
            const year = parts[0];
            if (isNaN(year) || parts.length !== 3) {
                dateErrorMsg = 'Please select a valid date.';
            } else if (year < currentYear) {
                dateErrorMsg = 'Event Date cannot be in the past. Please choose a future date.';
            } else if (year > maxAllowedYear) {
                dateErrorMsg = `Event Year cannot be beyond ${maxAllowedYear}.`;
            } else {
                const selectedD = new Date(parts[0], parts[1] - 1, parts[2]);
                const todayD = new Date();
                todayD.setHours(0, 0, 0, 0);
                if (selectedD < todayD) {
                    dateErrorMsg = 'Event Date cannot be in the past. Please choose a future date.';
                }
            }
        }

        if (dateErrorMsg) {
            setTouched(prev => ({ ...prev, eventDate: true }));
            setErrors(prev => ({ ...prev, eventDate: dateErrorMsg }));
            showToast(dateErrorMsg, 'error', 3500);
            dateInputRef.current?.focus();
            return;
        }

        // 4. Time Validation: Show toast & auto-focus time field if invalid
        let timeErrorMsg = '';
        if (!eventTimePart) {
            timeErrorMsg = 'Event Time is required. Please select an event time.';
        } else if (eventDatePart) {
            const parts = eventDatePart.split('-').map(Number);
            const [hStr, mStr] = eventTimePart.split(':');
            const combinedDate = new Date(parts[0], parts[1] - 1, parts[2], parseInt(hStr, 10), parseInt(mStr, 10));
            if (isNaN(combinedDate.getTime())) {
                timeErrorMsg = 'Please select a valid time.';
            } else if (combinedDate < new Date()) {
                timeErrorMsg = 'Selected time has already passed today. Please choose a future time.';
            }
        }

        if (timeErrorMsg) {
            setTouched(prev => ({ ...prev, eventTime: true }));
            setErrors(prev => ({ ...prev, eventTime: timeErrorMsg }));
            showToast(timeErrorMsg, 'error', 3500);
            if (isCustomTime) {
                timeInputRef.current?.focus();
            } else {
                timeSelectRef.current?.focus();
            }
            return;
        }

        const allTouched = {
            eventType: true,
            eventName: true,
            hostName: true,
            eventDate: true,
            eventTime: true,
            eventVenue: true,
            eventCity: true,
            customEventType: true
        };
        setTouched(allTouched);
        if (validateStep1(allTouched)) {
            // If logged in and WhatsApp is not linked, guide user to connect first
            if (inviteUser && whatsappStatus !== 'connected') {
                setShowWaStep1Modal(true);
                return;
            }
            setCurrentStep(2);
            setErrors({});
            setTouched({});
        } else {
            showToast('Please fill in all required event details.', 'error', 3000);
        }
    };

    const handleNextStep2 = () => {
        if (validateStep2()) {
            setCurrentStep(3);
            setErrors({});
            setTouched({});
        }
    };
    // Fetch suggestions locally or via backend Suggestions endpoint
    useEffect(() => {
        if (currentStep === 2 && eventType) {
            const fetchSuggestions = async () => {
                setFetchingSuggestions(true);
                try {
                    // Try anonymous templates fetch
                    const response = await fetch(`${API_BASE_URL}/api/invite/templates?eventType=${eventType}&tone=${tone}&language=${language}`);
                    if (response.ok) {
                        const data = await response.json();
                        const list = data.templates && data.templates.length > 0 ? data.templates : DEFAULT_FALLBACK_TEMPLATES;
                        setSuggestions(list);
                        if (list.length > 0 && !messageTemplate) {
                            setMessageTemplate(list[0].content);
                        }
                    } else {
                        setSuggestions(DEFAULT_FALLBACK_TEMPLATES);
                        if (!messageTemplate) {
                            setMessageTemplate(DEFAULT_FALLBACK_TEMPLATES[0].content);
                        }
                    }
                } catch (err) {
                    console.error('Error fetching suggestions, using ready-made fallback:', err);
                    setSuggestions(DEFAULT_FALLBACK_TEMPLATES);
                    if (!messageTemplate) {
                        setMessageTemplate(DEFAULT_FALLBACK_TEMPLATES[0].content);
                    }
                } finally {
                    setFetchingSuggestions(false);
                }
            };
            fetchSuggestions();
        }
    }, [currentStep, eventType, tone, language]);

    // Load Google script when the overlay is shown (anonymous + > 10 recipients) in Step 3
    useEffect(() => {
        if (currentStep === 3 && !inviteUser && recipients.length > 10) {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);

            script.onload = () => {
                const google = (window as any).google;
                if (google) {
                    google.accounts.id.initialize({
                        client_id: GOOGLE_CLIENT_ID,
                        callback: handleGoogleAuthResponse,
                    });
                    google.accounts.id.renderButton(
                        document.getElementById('wizard-google-btn'),
                        { theme: 'outline', size: 'large', width: '100%' }
                    );
                }
            };

            return () => {
                document.body.removeChild(script);
            };
        }
    }, [currentStep, inviteUser, recipients.length]);    // Handle Google verification inside wizard
    const handleGoogleAuthResponse = async (response: any) => {
        setLoading(true);
        setAuthError(null);
        try {
            await inviteGoogleLogin(response.credential);
        } catch (err: any) {
            setAuthError(err.message || 'Google Auth failed');
        } finally {
            setLoading(false);
        }
    };

    // Handle standard login/register within wizard
    const handleAuthSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError(null);
        setLoading(true);

        try {
            if (authMode === 'login') {
                await inviteLogin(authEmail, authPassword);
            } else {
                await inviteRegister(authName, authEmail, authPassword);
            }
        } catch (err: any) {
            setAuthError(err.message || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    // Add manual guest
    const handleAddManualRecipient = (e: React.FormEvent) => {
        e.preventDefault();
        
        const maxLimit = inviteUser?.maxInvitations || 10;
        const totalSent = inviteUser?.totalInvitesSent || 0;
        const remainingQuota = typeof inviteUser?.remainingInvites === 'number' 
            ? inviteUser.remainingInvites 
            : Math.max(0, maxLimit - totalSent);

        if (recipients.length >= maxLimit) {
            if (window.confirm(`Plan Limit Reached: Your plan limit is ${maxLimit} guests per campaign. Would you like to upgrade your package to add more guests?`)) {
                history.push('/invite/upgrade');
            }
            return;
        }

        if (recipients.length >= remainingQuota) {
            if (window.confirm(`Quota Exhausted: You have ${remainingQuota} invitation credits remaining out of your ${maxLimit} plan quota (${totalSent} already sent). Would you like to upgrade your package to increase your capacity?`)) {
                history.push('/invite/upgrade');
            }
            return;
        }

        const allTouched = { ...touched, manualName: true, manualPhone: true, manualEmail: true };
        setTouched(allTouched);
        
        if (validateManualGuest(allTouched, manualName, manualPhone, manualEmail)) {
            const newRec: Recipient = {
                name: manualName.trim(),
                phone: manualPhone.replace(/[\s\-().]/g, ''),
                email: manualEmail.trim() || undefined,
                telegramChatId: manualTelegram.trim() || undefined,
            };

            setRecipients(prev => [...prev, newRec]);
            resetManualGuestForm();
            setIsGuestModalOpen(false);
            showToast(`Successfully added ${newRec.name}!`, 'success', 2000);
        }
    };

    // Stateless file upload parsing — with client-side phone & email validation
    const handleFileUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!excelFile) return;

        setLoading(true);
        setImportLogs(null);
        try {
            const formData = new FormData();
            formData.append('invitationExcel', excelFile);

            const response = await fetch(`${API_BASE_URL}/api/invite/recipients/parse-file`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();
            if (response.ok) {
                const rawList: Recipient[] = data.recipients;
                const valid: Recipient[] = [];
                const skipped: { name: string; reason: string }[] = [];

                const maxLimit = inviteUser?.maxInvitations || 10;
                const totalSent = inviteUser?.totalInvitesSent || 0;
                const remainingQuota = typeof inviteUser?.remainingInvites === 'number' 
                    ? inviteUser.remainingInvites 
                    : Math.max(0, maxLimit - totalSent);

                rawList.forEach((r) => {
                    // Enforce plan guest limit and remaining quota
                    if ((recipients.length + valid.length) >= maxLimit) {
                        skipped.push({
                            name: r.name || 'Unknown',
                            reason: `Plan guest limit reached (max ${maxLimit} guests)`,
                        });
                        return;
                    }

                    if ((recipients.length + valid.length) >= remainingQuota) {
                        skipped.push({
                            name: r.name || 'Unknown',
                            reason: `Quota exhausted (${remainingQuota} credits remaining out of ${maxLimit})`,
                        });
                        return;
                    }

                    // Validate phone
                    if (!r.phone || !isValidPhone(r.phone)) {
                        skipped.push({
                            name: r.name || 'Unknown',
                            reason: `Invalid phone "${r.phone || '(blank)'}" — must be 10–15 digits`,
                        });
                        return;
                    }
                    // Validate email (only if provided)
                    if (r.email && !isValidEmail(r.email)) {
                        skipped.push({
                            name: r.name || 'Unknown',
                            reason: `Invalid email "${r.email}"`,
                        });
                        return;
                    }
                    // Duplicate check
                    const sanitized = r.phone.replace(/[\s\-().]/g, '');
                    if (recipients.some(ex => ex.phone.replace(/[\s\-().]/g, '') === sanitized)) {
                        skipped.push({
                            name: r.name || 'Unknown',
                            reason: `Duplicate phone "${r.phone}"`,
                        });
                        return;
                    }
                    valid.push(r);
                });

                setRecipients(prev => [...prev, ...valid]);

                // Show top-right floating toast notification (auto-dismisses in ~2s)
                if (valid.length > 0) {
                    showToast(`Successfully added ${valid.length} guest${valid.length !== 1 ? 's' : ''}!`, 'success', 2000);
                } else if (skipped.length > 0) {
                    showToast(`No new guests added (${skipped.length} skipped)`, 'error', 2000);
                }

                // If rows were skipped, keep a warning message; otherwise clear importLogs
                if (skipped.length > 0) {
                    const warnMsg = `⚠️ ${skipped.length} row${skipped.length !== 1 ? 's' : ''} skipped:\n` + skipped.map(s => `  • ${s.name}: ${s.reason}`).join('\n');
                    setImportLogs(warnMsg);
                } else {
                    setImportLogs(null);
                }
            } else {
                alert(data.message || 'Failed to parse file');
            }
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(false);
            setExcelFile(null);
        }
    };

    const handleDeleteRecipient = (index: number) => {
        const guestName = recipients[index]?.name || 'this guest';
        if (window.confirm(`Are you sure you want to remove ${guestName} from the guest list?`)) {
            const updated = recipients.filter((_, i) => i !== index);
            setRecipients(updated);
            if (updated.length === 0) {
                setImportLogs(null);
            }
            if (editingIndex === index) { setEditingIndex(null); }
        }
    };

    // ── Inline Edit Handlers ───────────────────────────────────────
    const handleStartEdit = (index: number) => {
        setEditingIndex(index);
        setEditForm({ ...recipients[index] });
        setEditErrors({});
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditErrors({});
    };

    const handleSaveEdit = () => {
        const errs: Record<string, string> = {};
        if (!editForm.name.trim()) errs.name = 'Name is required.';
        if (!editForm.phone.trim()) {
            errs.phone = 'Phone is required.';
        } else if (!isValidPhone(editForm.phone)) {
            errs.phone = 'Enter a valid phone number with at least 10 digits.';
        } else {
            // Check duplicate (exclude current row)
            const sanitized = editForm.phone.replace(/[\s\-().]/g, '');
            if (recipients.some((r, i) => i !== editingIndex && r.phone.replace(/[\s\-().]/g, '') === sanitized)) {
                errs.phone = 'This phone number already exists in the list.';
            }
        }
        if (editForm.email && !isValidEmail(editForm.email)) {
            errs.email = 'Enter a valid email address.';
        }
        setEditErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setRecipients(prev =>
            prev.map((r, i) =>
                i === editingIndex
                    ? {
                          ...r,
                          name: editForm.name.trim(),
                          phone: editForm.phone.replace(/[\s\-().]/g, ''),
                          email: editForm.email?.trim() || undefined,
                          telegramChatId: editForm.telegramChatId?.trim() || undefined,
                      }
                    : r
            )
        );
        setEditingIndex(null);
        setEditErrors({});
    };

    // Test send (via standalone dummy endpoint or simulate)
    const handleTestSend = async () => {
        if (!testPhone) {
            alert('Please enter a phone number for the test.');
            return;
        }

        setLoading(true);
        setTestResult(null);
        try {
            // Call test dispatch (stateless sending requires creating a mock campaign on backend)
            const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/send-anonymous`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventType,
                    eventName,
                    eventDate,
                    eventVenue,
                    eventCity,
                    tone,
                    language,
                    messageTemplate,
                    channels,
                    recipients: [{ name: 'Test Guest', phone: testPhone }],
                }),
            });

            if (response.ok) {
                setTestResult('🎉 Test message sent successfully!');
            } else {
                const data = await response.json();
                setTestResult(`❌ Failed to send: ${data.message}`);
            }
        } catch (err: any) {
            setTestResult(`❌ Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Final Dispatch All
    const handleFinalSend = async () => {
        // Guard: check if WhatsApp is selected and unlinked
        if (channels.includes('whatsapp') && whatsappStatus !== 'connected') {
            setShowWaDispatchModal(true);
            return;
        }

        setSendLoading(true);
        try {
            if (inviteUser) {
                // AUTHENTICATED DISPATCH FLOW:
                let campaignId = editCampaignId;
                if (campaignId) {
                    // 1. Update existing campaign
                    const campRes = await fetch(`${API_BASE_URL}/api/invite/campaigns/${campaignId}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${inviteToken}`,
                        },
                        body: JSON.stringify({
                            eventType,
                            eventName,
                            hostName: hostName.trim() || undefined,
                            collectDietary,
                            collectAccommodation,
                            eventDate,
                            eventVenue,
                            eventCity,
                            tone,
                            language,
                            messageTemplate,
                            mediaUrl: mediaUrl || undefined,
                            mediaType: mediaType || 'none',
                            reminderEnabled: Boolean(reminderEnabled),
                            channels,
                            notificationChannel,
                        }),
                    });
                    if (!campRes.ok) throw new Error('Failed to update invitation record');
                } else {
                    // 1. Create campaign
                    const campRes = await fetch(`${API_BASE_URL}/api/invite/campaigns`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${inviteToken}`,
                        },
                        body: JSON.stringify({
                            eventType,
                            eventName,
                            hostName: hostName.trim() || undefined,
                            collectDietary,
                            collectAccommodation,
                            eventDate,
                            eventVenue,
                            eventCity,
                            tone,
                            language,
                            messageTemplate,
                            mediaUrl: mediaUrl || undefined,
                            mediaType: mediaType || 'none',
                            reminderEnabled: Boolean(reminderEnabled),
                            channels,
                            notificationChannel,
                        }),
                    });
                    if (!campRes.ok) throw new Error('Failed to create invitation record');
                    const campData = await campRes.json();
                    campaignId = campData.campaign._id;
                }

                // 2. Add recipients (using replaceAll: true to clean existing ones when editing)
                const recRes = await fetch(`${API_BASE_URL}/api/invite/campaigns/${campaignId}/recipients`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${inviteToken}`,
                    },
                    body: JSON.stringify({ recipients, replaceAll: true }),
                });
                if (!recRes.ok) throw new Error('Failed to save recipients list');

                // 3. Trigger send
                const sendRes = await fetch(`${API_BASE_URL}/api/invite/campaigns/${campaignId}/send`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${inviteToken}` },
                });

                if (sendRes.ok) {
                    alert('🚀 Invitations sending started!');
                    localStorage.removeItem('shadigram_invite_wizard_draft');
                    history.push('/invite/dashboard');
                } else if (sendRes.status === 402) {
                    const data = await sendRes.json();
                    if (window.confirm(`${data.message}\n\nWould you like to upgrade your Shadigram package now to increase your limits?`)) {
                        history.push('/invite/upgrade');
                    }
                } else {
                    const data = await sendRes.json();
                    if (data.message && data.message.toLowerCase().includes('whatsapp is not connected')) {
                        setShowWaDispatchModal(true);
                    } else {
                        alert(data.message || 'Failed to dispatch invites');
                    }
                }
            } else {
                // ANONYMOUS DISPATCH FLOW (Recipients <= 10):
                const response = await fetch(`${API_BASE_URL}/api/invite/campaigns/send-anonymous`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        eventType,
                        eventName,
                        hostName: hostName.trim() || undefined,
                        collectDietary,
                        collectAccommodation,
                        eventDate,
                        eventVenue,
                        eventCity,
                        tone,
                        language,
                        messageTemplate,
                        mediaUrl: mediaUrl || undefined,
                        mediaType: mediaType || 'none',
                        channels,
                        recipients,
                        notificationChannel,
                    }),
                });

                const data = await response.json();
                if (response.ok) {
                    alert('🚀 Anonymous invitations dispatch completed!');
                    localStorage.removeItem('shadigram_invite_wizard_draft');
                    history.push(`/invite/summary/${data.campaignId}`);
                } else {
                    alert(data.message || 'Anonymous invitation send failed');
                }
            }
        } catch (err: any) {
            alert(err.message);
        } finally {
            setSendLoading(false);
        }
    };

    // Save Campaign Draft
    const handleSaveDraft = async () => {
        // Save locally first
        saveProgressToLocal(currentStep);

        if (!inviteUser) {
            alert('📁 Progress saved locally on this device! Log in or sign up to sync your draft to your account.');
            return;
        }

        // To save to database, we need the basic Step 1 fields
        const hasBasicDetails = !!eventName.trim() && !!hostName.trim() && !!eventVenue.trim() && !!eventCity.trim() && !!eventDate;

        if (!hasBasicDetails) {
            alert('📁 Saved locally on this device! Please complete Step 1 (Event Name, Host Family, Date, Venue, and City) to save it as a draft in your Shadigram account.');
            return;
        }

        setSaveLoading(true);
        try {
            let campaignId = editCampaignId;
            const campaignPayload = {
                eventType,
                eventName,
                hostName: hostName.trim() || undefined,
                collectDietary,
                collectAccommodation,
                eventDate,
                eventVenue,
                eventCity,
                tone,
                language,
                messageTemplate: messageTemplate || 'Dear {{name}}, you are cordially invited to our event. {{rsvpLink}}',
                mediaUrl: mediaUrl || undefined,
                mediaType: mediaType || 'none',
                reminderEnabled: Boolean(reminderEnabled),
                channels,
                notificationChannel,
            };

            if (campaignId) {
                // 1. Update existing campaign
                const campRes = await fetch(`${API_BASE_URL}/api/invite/campaigns/${campaignId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${inviteToken}`,
                    },
                    body: JSON.stringify(campaignPayload),
                });
                if (!campRes.ok) throw new Error('Failed to update invitation record');
            } else {
                // 1. Create campaign draft
                const campRes = await fetch(`${API_BASE_URL}/api/invite/campaigns`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${inviteToken}`,
                    },
                    body: JSON.stringify(campaignPayload),
                });
                if (!campRes.ok) throw new Error('Failed to create invitation record');
                const campData = await campRes.json();
                campaignId = campData.campaign._id;
                
                // Update URL parameters
                history.replace(`/invite/new?edit=${campaignId}`);
                
                // Update localStorage so future autosaves preserve editCampaignId
                const localDraft = localStorage.getItem('shadigram_invite_wizard_draft');
                if (localDraft) {
                    const parsed = JSON.parse(localDraft);
                    parsed.editCampaignId = campaignId;
                    localStorage.setItem('shadigram_invite_wizard_draft', JSON.stringify(parsed));
                }
            }

            // 2. Add recipients (if any)
            if (recipients.length > 0) {
                const recRes = await fetch(`${API_BASE_URL}/api/invite/campaigns/${campaignId}/recipients`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${inviteToken}`,
                    },
                    body: JSON.stringify({ recipients, replaceAll: true }),
                });
                if (!recRes.ok) throw new Error('Failed to save recipients list');
            }

            alert('📁 Invitation successfully saved as Draft in your Shadigram account!');
        } catch (err: any) {
            alert(`Error saving draft: ${err.message}`);
        } finally {
            setSaveLoading(false);
        }
    };

    // Helper: Interpolate template for preview
    const getPersonalizedPreview = () => {
        let msg = messageTemplate || '';
        const sampleName = recipients[0]?.name || 'Guest Name';
        const sampleRsvp = 'http://shadigram.com/invite/rsvp/sample-token';
        const dateStr = eventDate ? new Date(eventDate).toLocaleDateString() : 'Event Date';

        const placeholders: Record<string, string> = {
            '{{recipientName}}': sampleName,
            '{{name}}': sampleName,
            '{{eventName}}': eventName || 'My Special Event',
            '{{date}}': dateStr,
            '{{venue}}': eventVenue || 'My Event Venue',
            '{{city}}': eventCity || 'City',
            '{{rsvpLink}}': sampleRsvp,
        };

        for (const [key, value] of Object.entries(placeholders)) {
            msg = msg.replace(new RegExp(key, 'g'), value);
        }

        return msg;
    };

    return (
        <div className="invite-wizard-container">
            {/* Top-Right Floating Toast Notification */}
            {toast && (
                <div className={`wizard-toast wizard-toast-${toast.type}`} role="alert">
                    <span className="wizard-toast-icon">
                        {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}
                    </span>
                    <span className="wizard-toast-text">{toast.message}</span>
                </div>
            )}

            {/* Unsaved progress recovery banner */}
            {showRestoreBanner && (
                <div className="restore-draft-banner">
                    <div className="restore-banner-content">
                        <span className="restore-banner-icon">💡</span>
                        <div className="restore-banner-text">
                            <h4>Unsaved Progress Found</h4>
                            <p>We found unsaved progress for <strong>"{savedProgress?.eventName || 'Untitled Event'}"</strong> from {new Date(savedProgress?.timestamp).toLocaleString()}.</p>
                        </div>
                    </div>
                    <div className="restore-banner-actions">
                        <button onClick={handleRestoreProgress} className="restore-btn">Restore Progress</button>
                        <button onClick={handleDismissProgress} className="dismiss-btn">Discard</button>
                    </div>
                </div>
            )}



            {/* Step Header */}
            <div className="wizard-progress-bar">
                <div className={`step-dot ${currentStep >= 1 ? 'active' : ''}`}>1<span>Setup & Guests</span></div>
                <div className={`step-line ${currentStep >= 2 ? 'active' : ''}`}></div>
                <div className={`step-dot ${currentStep >= 2 ? 'active' : ''}`}>2<span>Template</span></div>
                <div className={`step-line ${currentStep >= 3 ? 'active' : ''}`}></div>
                <div className={`step-dot ${currentStep >= 3 ? 'active' : ''}`}>3<span>Preview & Send</span></div>
            </div>

            {/* Mobile-Friendly Active Step Banner */}
            <div className="wizard-mobile-step-indicator">
                <span className="mobile-step-pill">Step {currentStep} of 3</span>
                <span className="mobile-step-label">
                    {currentStep === 1 && '💒 Setup & Guests'}
                    {currentStep === 2 && '🎨 Template & AI Studio'}
                    {currentStep === 3 && '🚀 Preview & Send'}
                </span>
            </div>

            {/* STEP 1: EVENT DETAILS */}
            {currentStep === 1 && (
                <div className="wizard-step-card">
                    <h2>💒 Step 1: Tell us about your event</h2>
                    <p>Enter details that will fill placeholders in your invitation templates.</p>


                    <div className="form-grid">
                        <div className="form-group">
                            <label>Event Type *</label>
                            <select 
                                value={eventType} 
                                onChange={(e) => {
                                    setEventType(e.target.value);
                                    const newTouched = { ...touched, eventType: true };
                                    setTouched(newTouched);
                                    validateStep1(newTouched);
                                }}
                                onBlur={() => {
                                    const newTouched = { ...touched, eventType: true };
                                    setTouched(newTouched);
                                    validateStep1(newTouched);
                                }}
                                className={`${!eventType ? 'select-placeholder' : ''} ${touched.eventType && errors.eventType ? 'input-error' : ''}`}
                                required
                            >
                                <option value="" disabled>Select Event</option>
                                <option value="marriage">Marriage / Wedding</option>
                                <option value="birthday">Birthday Party</option>
                                <option value="housewarming">Housewarming Ceremony</option>
                                <option value="anniversary">Wedding Anniversary</option>
                                <option value="corporate">Corporate Event</option>
                                <option value="engagement">Engagement Ceremony</option>
                                <option value="custom">Custom Event</option>
                            </select>
                            {touched.eventType && errors.eventType && <span className="validation-error">⚠️ {errors.eventType}</span>}
                        </div>

                        {eventType === 'custom' && (
                            <div className="form-group">
                                <label>Custom Event Type Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Baby Shower, Graduation"
                                    value={customEventType}
                                    onChange={(e) => {
                                        setCustomEventType(e.target.value);
                                        if (touched.customEventType) validateStep1({ ...touched, customEventType: true });
                                    }}
                                    onBlur={() => {
                                        const newTouched = { ...touched, customEventType: true };
                                        setTouched(newTouched);
                                        validateStep1(newTouched);
                                    }}
                                    className={touched.customEventType && errors.customEventType ? 'input-error' : ''}
                                />
                                {touched.customEventType && errors.customEventType && <span className="validation-error">⚠️ {errors.customEventType}</span>}
                            </div>
                        )}

                        <div className="form-group">
                            <label>Event Title Name *</label>
                            <input
                                type="text"
                                placeholder="e.g. Raj & Simran's Wedding"
                                value={eventName}
                                onChange={(e) => {
                                    setEventName(e.target.value);
                                    if (touched.eventName) validateStep1({ ...touched, eventName: true });
                                }}
                                onBlur={() => {
                                    const newTouched = { ...touched, eventName: true };
                                    setTouched(newTouched);
                                    validateStep1(newTouched);
                                }}
                                className={touched.eventName && errors.eventName ? 'input-error' : ''}
                                required
                            />
                            {touched.eventName && errors.eventName && <span className="validation-error">⚠️ {errors.eventName}</span>}
                        </div>

                        <div className="form-group">
                            <label>Host Family / Hosts *</label>
                            <input
                                type="text"
                                placeholder="e.g. The Parsai Family, Nitin & Pooja"
                                value={hostName}
                                onChange={(e) => {
                                    setHostName(e.target.value);
                                    if (touched.hostName) validateStep1({ ...touched, hostName: true });
                                }}
                                onBlur={() => {
                                    const newTouched = { ...touched, hostName: true };
                                    setTouched(newTouched);
                                    validateStep1(newTouched);
                                }}
                                className={touched.hostName && errors.hostName ? 'input-error' : ''}
                                required
                            />
                            {touched.hostName && errors.hostName && <span className="validation-error">⚠️ {errors.hostName}</span>}
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Calendar size={15} color="#8B5CF6" /> Event Date *
                            </label>
                            <div className="picker-input-container">
                                <input
                                    ref={dateInputRef}
                                    type="date"
                                    min={todayStr}
                                    max={maxDateStr}
                                    value={eventDatePart}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        updateDateTime(val, eventTimePart);
                                        const newTouched = { ...touched, eventDate: true };
                                        setTouched(newTouched);
                                        validateStep1(newTouched, val, eventTimePart);
                                        if (val) {
                                            const parts = val.split('-').map(Number);
                                            const selectedD = new Date(parts[0], parts[1] - 1, parts[2]);
                                            const todayD = new Date();
                                            todayD.setHours(0, 0, 0, 0);
                                            if (selectedD < todayD) {
                                                showToast('Event Date cannot be in the past. Please select a future date.', 'error', 3000);
                                                setTimeout(() => dateInputRef.current?.focus(), 50);
                                            }
                                        }
                                    }}
                                    onClick={(e) => {
                                        try {
                                            (e.currentTarget as any).showPicker?.();
                                        } catch (err) {}
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Tab' || e.key === 'Escape' || e.key === 'Enter') {
                                            return;
                                        }
                                        e.preventDefault();
                                        try {
                                            (e.currentTarget as any).showPicker?.();
                                        } catch (err) {}
                                    }}
                                    onBlur={() => {
                                        const newTouched = { ...touched, eventDate: true };
                                        setTouched(newTouched);
                                        validateStep1(newTouched, eventDatePart, eventTimePart);
                                        if (eventDatePart) {
                                            const parts = eventDatePart.split('-').map(Number);
                                            const selectedD = new Date(parts[0], parts[1] - 1, parts[2]);
                                            const todayD = new Date();
                                            todayD.setHours(0, 0, 0, 0);
                                            if (selectedD < todayD) {
                                                showToast('Event Date cannot be in the past. Please select a future date.', 'error', 3000);
                                                dateInputRef.current?.focus();
                                            }
                                        }
                                    }}
                                    className={`${!eventDatePart ? 'select-placeholder' : ''} ${touched.eventDate && errors.eventDate ? 'input-error' : ''}`}
                                    required
                                />
                            </div>
                            {eventDatePart && (
                                <span className="picker-preview-badge">
                                    📅 {formatHumanDate(eventDatePart)}
                                </span>
                            )}
                            {touched.eventDate && errors.eventDate && <span className="validation-error">⚠️ {errors.eventDate}</span>}
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Clock size={15} color="#8B5CF6" /> Event Time *
                            </label>
                            <div className="picker-input-container">
                                <select
                                    ref={timeSelectRef}
                                    value={isCustomTime ? 'custom' : eventTimePart}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        let newTime = val;
                                        if (val === 'custom') {
                                            setIsCustomTime(true);
                                            newTime = '';
                                        } else {
                                            setIsCustomTime(false);
                                        }
                                        updateDateTime(eventDatePart, newTime);
                                        const newTouched = { ...touched, eventTime: true };
                                        setTouched(newTouched);
                                        validateStep1(newTouched, eventDatePart, newTime);
                                    }}
                                    onBlur={() => {
                                        const newTouched = { ...touched, eventTime: true };
                                        setTouched(newTouched);
                                        validateStep1(newTouched, eventDatePart, eventTimePart);
                                    }}
                                    className={`${!eventTimePart ? 'select-placeholder' : ''} ${touched.eventTime && errors.eventTime ? 'input-error' : ''}`}
                                    required
                                >
                                    <option value="" disabled>Select Time</option>
                                    <optgroup label="🌅 Morning (6:00 AM – 11:45 AM)">
                                        {TIME_INTERVALS.filter(t => t.group === 'morning').map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="☀️ Afternoon (12:00 PM – 4:45 PM)">
                                        {TIME_INTERVALS.filter(t => t.group === 'afternoon').map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="🌙 Evening & Night (5:00 PM – 11:45 PM)">
                                        {TIME_INTERVALS.filter(t => t.group === 'evening').map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="✨ Midnight & Early (12:00 AM – 5:45 AM)">
                                        {TIME_INTERVALS.filter(t => t.group === 'night').map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="⏱️ Custom">
                                        <option value="custom">Custom Time (Specific Minute)...</option>
                                    </optgroup>
                                </select>
                                {isCustomTime && (
                                    <div style={{ marginTop: '8px' }}>
                                        <input
                                            ref={timeInputRef}
                                            type="time"
                                            value={eventTimePart}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                updateDateTime(eventDatePart, val);
                                                const newTouched = { ...touched, eventTime: true };
                                                setTouched(newTouched);
                                                validateStep1(newTouched, eventDatePart, val);
                                            }}
                                            onBlur={() => {
                                                const newTouched = { ...touched, eventTime: true };
                                                setTouched(newTouched);
                                                validateStep1(newTouched, eventDatePart, eventTimePart);
                                            }}
                                            className={touched.eventTime && errors.eventTime ? 'input-error' : ''}
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                            {eventTimePart && !isCustomTime && (
                                <span className="picker-preview-badge">
                                    ⏰ {formatHumanTime(eventTimePart)}
                                </span>
                            )}
                            {touched.eventTime && errors.eventTime && <span className="validation-error">⚠️ {errors.eventTime}</span>}
                        </div>

                        <div className="form-group">
                            <label>Venue * <span className="tooltip-icon" title="Please enter the complete address containing the city name" style={{ cursor: 'help', color: '#C084FC', fontSize: '0.8rem', marginLeft: '4px' }}>ⓘ</span></label>
                            <input
                                type="text"
                                placeholder="e.g. Grand Palace Resort, Colaba, Mumbai"
                                value={eventVenue}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setEventVenue(val);
                                    
                                    // Automatically extract city from venue string
                                    let cityVal = '';
                                    const parts = val.split(',');
                                    if (parts.length > 1) {
                                        let last = parts[parts.length - 1].trim();
                                        if (/\d+/.test(last) && parts.length > 2) {
                                            cityVal = parts[parts.length - 2].trim();
                                        } else {
                                            cityVal = last;
                                        }
                                    } else {
                                        const words = val.trim().split(/\s+/);
                                        cityVal = words[words.length - 1] || '';
                                    }
                                    setEventCity(cityVal);

                                    if (touched.eventVenue) {
                                        validateStep1({ ...touched, eventVenue: true, eventCity: true });
                                    }
                                }}
                                onBlur={() => {
                                    const newTouched = { ...touched, eventVenue: true, eventCity: true };
                                    setTouched(newTouched);
                                    validateStep1(newTouched);
                                }}
                                className={touched.eventVenue && errors.eventVenue ? 'input-error' : ''}
                                required
                            />
                            {touched.eventVenue && errors.eventVenue && <span className="validation-error">⚠️ {errors.eventVenue}</span>}
                        </div>

                        <div className="form-group">
                            <label>Invitation Tone</label>
                            <select value={tone} onChange={(e) => setTone(e.target.value as any)}>
                                <option value="warm">Warm & Welcoming</option>
                                <option value="casual">Friendly & Casual</option>
                                <option value="formal">Elegant & Formal</option>
                                <option value="humorous">Fun & Humorous</option>
                                <option value="poetic">Artistic & Poetic</option>
                            </select>
                        </div>

                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Globe size={15} color="#8B5CF6" /> Invitation Language &amp; Templates
                            </label>
                            <select 
                                value={language} 
                                onChange={(e) => {
                                    setLanguage(e.target.value);
                                    setMessageTemplate('');
                                }}
                                style={{
                                    fontWeight: 600,
                                    color: isLightTheme ? '#0F172A' : '#F8FAFC',
                                    background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.8)',
                                    border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                                }}
                            >
                                {SUPPORTED_LANGUAGES.map(l => (
                                    <option key={l.code} value={l.code}>
                                        {l.flag} {l.nativeName} ({l.label})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* RSVP Feature Settings */}
                    <div style={{
                        marginTop: '1.5rem',
                        marginBottom: '2rem',
                        padding: '1.25rem',
                        borderRadius: '16px',
                        background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.75)',
                        border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(139, 92, 246, 0.3)',
                    }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', fontWeight: 800, color: isLightTheme ? '#334155' : '#D8B4FE', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📋 Guest RSVP Page Options</span>
                        </h4>
                        <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                            Choose what information you'd like your guests to provide when they respond to their invitation.
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                padding: '0.85rem 1rem',
                                borderRadius: '12px',
                                background: isLightTheme ? (collectDietary ? '#EDE9FE' : '#FFFFFF') : (collectDietary ? 'rgba(139, 92, 246, 0.15)' : 'rgba(30, 41, 59, 0.5)'),
                                border: collectDietary ? '1.5px solid #8B5CF6' : (isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.1)'),
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={collectDietary}
                                    onChange={(e) => setCollectDietary(e.target.checked)}
                                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#8B5CF6', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: isLightTheme ? '#1E293B' : '#FFFFFF' }}>🍲 Dietary Preferences</div>
                                    <div style={{ fontSize: '0.75rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginTop: '2px' }}>Ask guests for Veg, Non-Veg, Jain, Vegan, etc.</div>
                                </div>
                            </label>

                            <label style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                                padding: '0.85rem 1rem',
                                borderRadius: '12px',
                                background: isLightTheme ? (collectAccommodation ? '#EDE9FE' : '#FFFFFF') : (collectAccommodation ? 'rgba(139, 92, 246, 0.15)' : 'rgba(30, 41, 59, 0.5)'),
                                border: collectAccommodation ? '1.5px solid #8B5CF6' : (isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.1)'),
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={collectAccommodation}
                                    onChange={(e) => setCollectAccommodation(e.target.checked)}
                                    style={{ width: '18px', height: '18px', marginTop: '2px', accentColor: '#8B5CF6', cursor: 'pointer' }}
                                />
                                <div>
                                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: isLightTheme ? '#1E293B' : '#FFFFFF' }}>🏨 Accommodation &amp; Travel</div>
                                    <div style={{ fontSize: '0.75rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginTop: '2px' }}>Ask if guests need hotel stay, arrival date/time &amp; travel info.</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Add Guest Management integrated directly into Step 1 */}
                    <div className="step-guests-management">
                        <h3>👥 Step 1.2: Add Guests</h3>
                        <p style={{ fontSize: '0.85rem', color: '#94A3B8', marginBottom: '1.5rem' }}>
                            Upload your contact sheet or add guests manually to populate the delivery list.
                        </p>

                        <div className="step-guests-options">
                            {/* Option 1: Excel/CSV Import */}
                            <div className="guest-option-card">
                                <h4>Option 1 – Spreadsheet Import (Excel/CSV)</h4>
                                <p>Import multiple guests at once by downloading our template, filling in details, and uploading it back.</p>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', padding: '1rem', borderRadius: '12px', background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.6)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isLightTheme ? '#334155' : '#CBD5E1' }}>1. Download formatted template:</span>
                                        <a href={`${API_BASE_URL}/api/invite/templates/download`} className="download-template-btn" style={{ textDecoration: 'none', textAlign: 'center', padding: '6px 12px', fontSize: '0.8rem', margin: 0 }}>
                                            📥 Download Excel
                                        </a>
                                    </div>
                                    
                                    <hr style={{ border: 'none', borderTop: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', margin: '0.15rem 0' }} />
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: isLightTheme ? '#334155' : '#CBD5E1' }}>2. Upload completed spreadsheet:</span>
                                        <form onSubmit={handleFileUpload} style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                            <label 
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '0.6rem 0.9rem',
                                                    borderRadius: '10px',
                                                    border: isLightTheme ? '1.5px dashed #CBD5E1' : '1.5px dashed rgba(139, 92, 246, 0.35)',
                                                    background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.7)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    color: isLightTheme ? '#0F172A' : '#F8FAFC'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.borderColor = '#8B5CF6';
                                                    e.currentTarget.style.background = isLightTheme ? '#F5F3FF' : 'rgba(139, 92, 246, 0.1)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.borderColor = isLightTheme ? '#CBD5E1' : 'rgba(139, 92, 246, 0.35)';
                                                    e.currentTarget.style.background = isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.7)';
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                                    <span style={{
                                                        background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                                        color: '#FFFFFF',
                                                        padding: '4px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
                                                    }}>
                                                        Choose File
                                                    </span>
                                                    <span style={{ fontSize: '0.8rem', color: excelFile ? '#34D399' : '#94A3B8', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', fontWeight: excelFile ? 600 : 400 }}>
                                                        {excelFile ? `📄 ${excelFile.name}` : 'Select spreadsheet (.xlsx, .csv)'}
                                                    </span>
                                                </div>
                                                {excelFile && (
                                                    <span style={{ fontSize: '0.72rem', color: '#A78BFA', fontWeight: 600 }}>Change</span>
                                                )}
                                                <input
                                                    type="file"
                                                    accept=".xlsx,.xls,.csv"
                                                    onChange={(e) => setExcelFile(e.target.files ? e.target.files[0] : null)}
                                                    style={{ display: 'none' }}
                                                />
                                            </label>
                                            <button 
                                                type="submit" 
                                                className="upload-file-btn" 
                                                disabled={loading || !excelFile} 
                                                style={{ 
                                                    padding: '0.65rem 1rem', 
                                                    fontSize: '0.85rem', 
                                                    fontWeight: 700,
                                                    background: excelFile ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'rgba(255, 255, 255, 0.06)',
                                                    color: excelFile ? '#FFFFFF' : '#64748B',
                                                    border: 'none',
                                                    borderRadius: '10px',
                                                    cursor: excelFile && !loading ? 'pointer' : 'not-allowed',
                                                    boxShadow: excelFile ? '0 4px 14px rgba(16, 185, 129, 0.35)' : 'none',
                                                    transition: 'all 0.2s ease',
                                                    marginTop: '0.2rem'
                                                }}
                                            >
                                                {loading ? '⏳ Uploading & Processing...' : '📤 Upload Contact List'}
                                            </button>
                                        </form>
                                        {importLogs && (
                                            <div className="import-skipped-logs" style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: '#F59E0B', fontWeight: 600, whiteSpace: 'pre-line' }}>
                                                {importLogs}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Option 2: Add Guest Manually */}
                            <div className="guest-option-card" style={{ justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                                <h4>Option 2 – Add Guest Manually</h4>
                                <p>Type guest details directly using our manual entry popup.</p>
                                <button type="button" onClick={() => { resetManualGuestForm(); setIsGuestModalOpen(true); }} className="wizard-save-draft-btn" style={{ width: 'auto', padding: '0.6rem 1.5rem', marginTop: '1rem' }}>
                                    ➕ Add Guest Manually
                                </button>
                            </div>
                        </div>

                        {/* Guest Preview Table */}
                        <div className="recipients-preview-table-section" style={{ marginTop: '2rem' }}>
                            <h4 style={{ fontWeight: 800, marginBottom: '0.75rem', fontFamily: "'Outfit', sans-serif" }}>Guests Added ({recipients.length})</h4>
                            {recipients.length === 0 ? (
                                <p className="no-guests-yet" style={{ fontSize: '0.85rem', color: '#94A3B8' }}>No guests added yet. Add guests to see the preview list.</p>
                            ) : (
                                <div className="preview-table-wrapper">
                                    <table className="recipients-table">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Phone</th>
                                                <th>Email</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recipients.map((rec, index) => (
                                                editingIndex === index ? (
                                                    <tr key={index} className="editing-row">
                                                        <td>
                                                            <input
                                                                className={`edit-cell-input ${editErrors.name ? 'input-error' : ''}`}
                                                                value={editForm.name}
                                                                onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                                                placeholder="Guest Name"
                                                            />
                                                            {editErrors.name && <span className="edit-field-error">{editErrors.name}</span>}
                                                        </td>
                                                        <td>
                                                            <input
                                                                className={`edit-cell-input ${editErrors.phone ? 'input-error' : ''}`}
                                                                value={editForm.phone}
                                                                onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                                                                placeholder="e.g. 9876543210"
                                                            />
                                                            {editErrors.phone && <span className="edit-field-error">{editErrors.phone}</span>}
                                                        </td>
                                                        <td>
                                                            <input
                                                                className="edit-cell-input"
                                                                value={editForm.email || ''}
                                                                onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                                                                placeholder="email (optional)"
                                                            />
                                                        </td>
                                                        <td className="edit-action-cell">
                                                            <button type="button" onClick={handleSaveEdit} className="save-row-btn">✅ Save</button>
                                                            <button type="button" onClick={handleCancelEdit} className="cancel-row-btn">✖ Cancel</button>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <tr key={index}>
                                                        <td style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 600 }}>{rec.name}</td>
                                                        <td style={{ color: isLightTheme ? '#334155' : '#94A3B8' }}>{rec.phone}</td>
                                                        <td style={{ color: isLightTheme ? '#64748B' : '#94A3B8' }}>{rec.email || '-'}</td>
                                                        <td className="row-action-cell">
                                                            <button type="button" onClick={() => handleStartEdit(index)} className="edit-row-btn">✏️ Edit</button>
                                                            <button type="button" onClick={() => handleDeleteRecipient(index)} className="delete-row-btn">🗑️ Remove</button>
                                                        </td>
                                                    </tr>
                                                )
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step Actions */}
                    <div className="step-actions" style={{ marginTop: '2.5rem' }}>
                        <button onClick={handleSaveDraft} className="wizard-save-draft-btn" disabled={loading}>
                            {loading ? 'Saving...' : '📁 Save Draft'}
                        </button>
                        <button onClick={handleNextStep1} className="wizard-next-btn">
                            Next: Choose Template ➡️
                        </button>
                    </div>
                </div>
            )}

            {/* Manual Guest Addition Modal Overlay Popup */}
            {isGuestModalOpen && (
                <div className="wizard-modal-overlay">
                    <div className="wizard-modal-container">
                        <div className="wizard-modal-header">
                            <h3>➕ Add Guest Manually</h3>
                            <button className="wizard-modal-close-btn" onClick={() => { resetManualGuestForm(); setIsGuestModalOpen(false); }}>&times;</button>
                        </div>
                        <form onSubmit={handleAddManualRecipient} className="manual-add-form">
                            <div className="wizard-modal-body">
                                <div className="form-group">
                                    <label>Guest Name *</label>
                                    <input 
                                        type="text" 
                                        placeholder="e.g. Ramesh" 
                                        value={manualName} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            setManualName(val);
                                            if (touched.manualName) validateManualGuest({ ...touched, manualName: true }, val, manualPhone, manualEmail);
                                        }} 
                                        onBlur={() => {
                                            const newTouched = { ...touched, manualName: true };
                                            setTouched(newTouched);
                                            validateManualGuest(newTouched, manualName, manualPhone, manualEmail);
                                        }} 
                                        className={touched.manualName && errors.manualName ? 'input-error' : ''}
                                        required 
                                    />
                                    {touched.manualName && errors.manualName && <span className="validation-error">⚠️ {errors.manualName}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Phone Number *</label>
                                    <input 
                                        type="tel" 
                                        placeholder="e.g. +91XXXXXXXXXX" 
                                        value={manualPhone} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            setManualPhone(val);
                                            if (touched.manualPhone) validateManualGuest({ ...touched, manualPhone: true }, manualName, val, manualEmail);
                                        }} 
                                        onBlur={() => {
                                            const newTouched = { ...touched, manualPhone: true };
                                            setTouched(newTouched);
                                            validateManualGuest(newTouched, manualName, manualPhone, manualEmail);
                                        }} 
                                        className={touched.manualPhone && errors.manualPhone ? 'input-error' : ''}
                                        required 
                                    />
                                    {touched.manualPhone && errors.manualPhone && <span className="validation-error">⚠️ {errors.manualPhone}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Email (Optional)</label>
                                    <input 
                                        type="email" 
                                        placeholder="e.g. ramesh@example.com" 
                                        value={manualEmail} 
                                        onChange={e => {
                                            const val = e.target.value;
                                            setManualEmail(val);
                                            if (touched.manualEmail) validateManualGuest({ ...touched, manualEmail: true }, manualName, manualPhone, val);
                                        }} 
                                        onBlur={() => {
                                            const newTouched = { ...touched, manualEmail: true };
                                            setTouched(newTouched);
                                            validateManualGuest(newTouched, manualName, manualPhone, manualEmail);
                                        }} 
                                        className={touched.manualEmail && errors.manualEmail ? 'input-error' : ''}
                                    />
                                    {touched.manualEmail && errors.manualEmail && <span className="validation-error">⚠️ {errors.manualEmail}</span>}
                                </div>
                            </div>
                            <div className="wizard-modal-footer">
                                <button type="button" className="dismiss-btn" onClick={() => { resetManualGuestForm(); setIsGuestModalOpen(false); }}>Cancel</button>
                                <button type="submit" className="wizard-next-btn">Save Guest</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* STEP 2: INVITATION TEMPLATE */}
            {currentStep === 2 && (
                <div className="wizard-step-card step2-minimal-container">
                    <button 
                        onClick={() => setCurrentStep(1)} 
                        className="wizard-back-top-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: '#8B5CF6', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, padding: 0, marginBottom: '1rem', outline: 'none' }}
                    >
                        ⬅️ Back to Step 1 (Setup & Guests)
                    </button>

                    <div className="step2-header-section" style={{ marginBottom: '1.5rem' }}>
                        <h2 style={{ margin: '0 0 0.35rem 0', fontSize: '1.5rem', fontWeight: 800 }}>🎨 Step 2: Choose your Invitation Template</h2>
                        <p style={{ margin: 0, color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.92rem' }}>
                            Select a curated template or generate with AI, then customize your wording below.
                        </p>
                    </div>

                    {/* Step 2 Segmented Control */}
                    <div className="step2-segmented-control" style={{
                        display: 'inline-flex',
                        background: isLightTheme ? '#F1F5F9' : 'rgba(15, 23, 42, 0.7)',
                        border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                        padding: '4px',
                        borderRadius: '12px',
                        marginBottom: '1.75rem',
                        gap: '4px'
                    }}>
                        <button
                            type="button"
                            onClick={() => setStep2Tab('library')}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '9px',
                                border: 'none',
                                fontSize: '0.88rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                background: step2Tab === 'library'
                                    ? (isLightTheme ? '#FFFFFF' : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)')
                                    : 'transparent',
                                color: step2Tab === 'library'
                                    ? (isLightTheme ? '#7C3AED' : '#FFFFFF')
                                    : (isLightTheme ? '#64748B' : '#94A3B8'),
                                boxShadow: step2Tab === 'library'
                                    ? (isLightTheme ? '0 2px 8px rgba(0,0,0,0.06)' : '0 2px 10px rgba(139, 92, 246, 0.3)')
                                    : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <span>📜 Curated Templates</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setStep2Tab('genai')}
                            style={{
                                padding: '8px 18px',
                                borderRadius: '9px',
                                border: 'none',
                                fontSize: '0.88rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                background: step2Tab === 'genai'
                                    ? 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)'
                                    : 'transparent',
                                color: step2Tab === 'genai' ? '#FFFFFF' : (isLightTheme ? '#64748B' : '#94A3B8'),
                                boxShadow: step2Tab === 'genai' ? '0 2px 12px rgba(236, 72, 153, 0.35)' : 'none',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <span>✨ AI Magic Studio</span>
                            <span style={{
                                fontSize: '0.68rem',
                                fontWeight: 800,
                                padding: '1px 6px',
                                borderRadius: '10px',
                                background: step2Tab === 'genai' ? 'rgba(255,255,255,0.25)' : (isLightTheme ? '#FEF3C7' : 'rgba(245, 158, 11, 0.2)'),
                                color: step2Tab === 'genai' ? '#FFFFFF' : '#D97706'
                            }}>
                                5 FREE
                            </span>
                        </button>
                    </div>

                    {step2Tab === 'genai' ? (
                        <div style={{ marginBottom: '2.5rem' }}>
                            <AiInvitationStudio
                                eventName={eventName}
                                eventType={eventType || customEventType || 'Celebration'}
                                hostName={hostName}
                                eventDate={eventDatePart || eventDate}
                                eventTime={eventTimePart}
                                eventVenue={eventVenue}
                                eventCity={eventCity}
                                initialTone={tone}
                                initialLanguage={language}
                                messageTemplate={messageTemplate}
                                onApplyTemplate={(tpl) => {
                                    setMessageTemplate(tpl);
                                    setTouched(prev => ({ ...prev, messageTemplate: true }));
                                    setErrors(prev => ({ ...prev, messageTemplate: '' }));
                                }}
                                onAttachMedia={handleMediaUpload}
                                isUploadingMedia={isUploadingMedia}
                                mediaUrl={mediaUrl}
                                mediaType={mediaType}
                                onRemoveMedia={() => {
                                    setMediaUrl('');
                                    setMediaType('none');
                                }}
                                isLightTheme={isLightTheme}
                                showToast={showToast}
                            />
                        </div>
                    ) : (
                        <>
                            {/* Unified Theme & Language Bar */}
                            <div className="step2-unified-toolbar" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '1rem',
                                padding: '0.85rem 1.15rem',
                                background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.6)',
                                border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '14px',
                                marginBottom: '1.5rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: isLightTheme ? '#475569' : '#CBD5E1', marginRight: '4px' }}>
                                        🎨 Theme:
                                    </span>
                                    {READY_MADE_CARD_THEMES.map((cTheme) => {
                                        const isThemeActive = tone === cTheme.id || selectedCardTheme === cTheme.id;
                                        return (
                                            <button
                                                key={cTheme.id}
                                                type="button"
                                                onClick={() => {
                                                    setTone(cTheme.id as any);
                                                    setSelectedCardTheme(cTheme.id);
                                                    setTouched(prev => ({ ...prev, tone: true }));
                                                }}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '6px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    border: isThemeActive
                                                        ? '1px solid #8B5CF6'
                                                        : (isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.1)'),
                                                    background: isThemeActive
                                                        ? (isLightTheme ? '#F5F3FF' : 'rgba(139, 92, 246, 0.25)')
                                                        : (isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.03)'),
                                                    color: isThemeActive
                                                        ? (isLightTheme ? '#7C3AED' : '#FFFFFF')
                                                        : (isLightTheme ? '#64748B' : '#94A3B8'),
                                                    transition: 'all 0.15s ease'
                                                }}
                                            >
                                                <span>{cTheme.icon}</span>
                                                <span>{cTheme.name}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: isLightTheme ? '#475569' : '#CBD5E1', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Globe size={14} color="#8B5CF6" />
                                    </span>
                                    <select 
                                        value={language} 
                                        onChange={(e) => {
                                            setLanguage(e.target.value);
                                            setMessageTemplate('');
                                        }} 
                                        style={{
                                            padding: '5px 10px',
                                            borderRadius: '8px',
                                            border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)',
                                            fontSize: '0.8rem',
                                            outline: 'none',
                                            background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.8)',
                                            color: isLightTheme ? '#0F172A' : '#F8FAFC',
                                            fontWeight: 600
                                        }}
                                    >
                                        {SUPPORTED_LANGUAGES.map(l => (
                                            <option key={l.code} value={l.code}>
                                                {l.flag} {l.nativeName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Template Suggestion Cards */}
                            <div className="template-preview-grid" style={{ marginBottom: '1.75rem' }}>
                                {fetchingSuggestions ? (
                                    <p style={{ gridColumn: '1/-1', textAlign: 'center', color: '#8B5CF6', padding: '1.5rem 0' }}>Loading template suggestions...</p>
                                ) : suggestions.length === 0 ? (
                                    <p style={{ gridColumn: '1/-1', textAlign: 'center', color: '#94A3B8', padding: '1.5rem 0' }}>No suggestions found. Customize your wording below.</p>
                                ) : (
                                    suggestions.map((sug, i) => {
                                        const isSelected = messageTemplate === sug.content;
                                        return (
                                            <div
                                                key={i}
                                                className={`template-preview-card ${isSelected ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setMessageTemplate(sug.content);
                                                    setTouched(prev => ({ ...prev, messageTemplate: true }));
                                                    setErrors(prev => ({ ...prev, messageTemplate: '' }));
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    borderRadius: '12px',
                                                    padding: '1rem',
                                                    border: isSelected 
                                                        ? '2px solid #8B5CF6' 
                                                        : (isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)'),
                                                    background: isSelected 
                                                        ? (isLightTheme ? '#F5F3FF' : 'rgba(139, 92, 246, 0.12)') 
                                                        : (isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.6)'),
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                                                    <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: isLightTheme ? (isSelected ? '#7C3AED' : '#0F172A') : (isSelected ? '#C084FC' : '#F8FAFC') }}>
                                                        {sug.title}
                                                    </h4>
                                                    {isSelected && (
                                                        <span style={{ fontSize: '0.72rem', background: '#8B5CF6', color: '#FFFFFF', padding: '2px 8px', borderRadius: '12px', fontWeight: 700 }}>
                                                            ✓ Selected
                                                        </span>
                                                    )}
                                                </div>
                                                <p style={{ 
                                                    margin: 0, 
                                                    fontSize: '0.8rem', 
                                                    color: isLightTheme ? '#64748B' : '#94A3B8', 
                                                    lineHeight: 1.45,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {sug.content}
                                                </p>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Clean Inline Message Wording Editor */}
                            <div className="step2-inline-editor-card" style={{
                                background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.65)',
                                border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '14px',
                                padding: '1.25rem',
                                marginBottom: '1.75rem'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                                    <label style={{ fontWeight: 700, color: isLightTheme ? '#0F172A' : '#F8FAFC', fontSize: '0.9rem' }}>
                                        ✏️ Customize Invitation Message:
                                    </label>
                                    <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                                        Tags like <code>{"{{name}}"}</code> auto-personalize per guest
                                    </span>
                                </div>

                                <textarea
                                    rows={7}
                                    value={messageTemplate}
                                    onChange={(e) => {
                                        setMessageTemplate(e.target.value);
                                        if (touched.messageTemplate) validateStep2();
                                    }}
                                    onBlur={() => {
                                        setTouched(prev => ({ ...prev, messageTemplate: true }));
                                        validateStep2();
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '0.85rem',
                                        borderRadius: '10px',
                                        border: (touched.messageTemplate && errors.messageTemplate) 
                                            ? '1.5px solid #EF4444' 
                                            : (isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)'),
                                        background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.85)',
                                        color: isLightTheme ? '#0F172A' : '#F8FAFC',
                                        fontFamily: 'inherit',
                                        fontSize: '0.88rem',
                                        lineHeight: 1.5,
                                        boxSizing: 'border-box',
                                        resize: 'vertical',
                                        outline: 'none'
                                    }}
                                    placeholder="Dear {{name}}, you are cordially invited..."
                                />

                                {touched.messageTemplate && errors.messageTemplate && (
                                    <div style={{ color: '#EF4444', fontSize: '0.78rem', marginTop: '4px', fontWeight: 600 }}>
                                        ⚠️ {errors.messageTemplate}
                                    </div>
                                )}
                                {errors.messageTemplateWarning && !errors.messageTemplate && (
                                    <div style={{ color: '#F59E0B', fontSize: '0.78rem', marginTop: '4px', fontWeight: 600 }}>
                                        {errors.messageTemplateWarning}
                                    </div>
                                )}

                                {/* Token Helpers */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '0.75rem', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#64748B' : '#94A3B8', fontWeight: 600, marginRight: '4px' }}>
                                        Insert Tag:
                                    </span>
                                    {[
                                        { label: '+ Guest Name', token: '{{name}}' },
                                        { label: '+ Host Family', token: '{{hostName}}' },
                                        { label: '+ Event Name', token: '{{eventName}}' },
                                        { label: '+ Date', token: '{{date}}' },
                                        { label: '+ Venue', token: '{{venue}}' },
                                        { label: '+ RSVP Link', token: '{{rsvpLink}}' },
                                    ].map((t) => (
                                        <button
                                            key={t.token}
                                            type="button"
                                            onClick={() => setMessageTemplate(prev => prev + ` ${t.token}`)}
                                            style={{
                                                padding: '4px 10px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                borderRadius: '6px',
                                                border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)',
                                                background: isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.05)',
                                                color: isLightTheme ? '#475569' : '#CBD5E1',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Minimalist Media Attachment Box */}
                            <div className="step2-media-box" style={{
                                background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.5)',
                                border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '14px',
                                padding: '1.15rem',
                                marginBottom: '2rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '0.65rem' }}>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '0.92rem', color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 700 }}>
                                            📎 Media Attachment (Optional)
                                        </h4>
                                        <p style={{ margin: '2px 0 0 0', fontSize: '0.78rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                                            Attach a photo poster or MP4 video invitation to dispatch alongside your message.
                                        </p>
                                    </div>
                                </div>

                                {mediaUrl ? (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.85rem',
                                        background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.8)',
                                        padding: '0.75rem 1rem',
                                        borderRadius: '10px',
                                        border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.1)'
                                    }}>
                                        {mediaType === 'video' ? (
                                            <video 
                                                src={mediaUrl.startsWith('http') || mediaUrl.startsWith('blob:') ? mediaUrl : `${API_BASE_URL}${mediaUrl}`} 
                                                style={{ width: '64px', height: '48px', borderRadius: '6px', objectFit: 'cover' }} 
                                            />
                                        ) : (
                                            <img 
                                                src={mediaUrl.startsWith('http') || mediaUrl.startsWith('blob:') ? mediaUrl : `${API_BASE_URL}${mediaUrl}`} 
                                                alt="Attached invite" 
                                                style={{ width: '64px', height: '48px', borderRadius: '6px', objectFit: 'cover' }} 
                                            />
                                        )}
                                        <div style={{ flex: 1 }}>
                                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>
                                                {mediaType === 'video' ? '🎬 Video Attached' : '🖼️ Photo Attached'}
                                            </span>
                                            <p style={{ margin: 0, fontSize: '0.72rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                                                Sent with your invitation on WhatsApp &amp; Telegram
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setMediaUrl(''); setMediaType('none'); }}
                                            style={{
                                                background: 'rgba(239, 68, 68, 0.1)',
                                                color: '#EF4444',
                                                border: '1px solid rgba(239, 68, 68, 0.25)',
                                                borderRadius: '6px',
                                                padding: '4px 10px',
                                                fontSize: '0.75rem',
                                                cursor: 'pointer',
                                                fontWeight: 600
                                            }}
                                        >
                                            🗑️ Remove
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
                                        <label style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            background: isLightTheme ? '#FFFFFF' : 'rgba(255, 255, 255, 0.04)',
                                            border: isLightTheme ? '1.5px dashed #8B5CF6' : '1px dashed rgba(139, 92, 246, 0.5)',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            cursor: isUploadingMedia ? 'not-allowed' : 'pointer',
                                            fontSize: '0.82rem',
                                            fontWeight: 600,
                                            color: '#8B5CF6'
                                        }}>
                                            <span>{isUploadingMedia ? '⏳ Uploading...' : '📁 Upload Photo or MP4 Video'}</span>
                                            <input
                                                type="file"
                                                accept="image/*,video/mp4,video/quicktime,video/webm"
                                                disabled={isUploadingMedia}
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        handleMediaUpload(e.target.files[0]);
                                                    }
                                                }}
                                            />
                                        </label>
                                        <span style={{ fontSize: '0.74rem', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                                            Supports JPG, PNG, WEBP, and MP4 (up to 25MB).
                                        </span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <div className="step-actions">
                        <button onClick={handleSaveDraft} className="wizard-save-draft-btn" disabled={loading}>
                            {loading ? 'Saving...' : '📁 Save Draft'}
                        </button>
                        <button onClick={handleNextStep2} className="wizard-next-btn">
                            Next: Preview & Send ➡️
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 3: PREVIEW, CHANNELS, SEND */}
            {currentStep === 3 && (
                <div className="wizard-step-card">
                    <button 
                        onClick={() => setCurrentStep(2)} 
                        className="wizard-back-top-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: '#8B5CF6', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, padding: 0, marginBottom: '1.25rem', outline: 'none' }}
                    >
                        ⬅️ Back to Step 2 (Invitation Template)
                    </button>
                    <h2>🚀 Step 3: Review & Dispatch Invitations</h2>
                    <p>Configure delivery channels, test your message, and final send.</p>

                    {/* Channel Selection Grid (Full-Width) */}
                    <div style={{ marginBottom: '2.5rem', borderBottom: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '2rem' }}>
                        <h3 style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 800, marginBottom: '1rem', fontSize: '1.25rem', fontFamily: "'Outfit', sans-serif" }}>🔌 Choose Delivery Channels</h3>
                        {errors.channels && (
                            <div className="validation-error-box" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.35)', borderRadius: '10px', color: '#FCA5A5', fontSize: '0.9rem' }}>
                                ⚠️ {errors.channels}
                            </div>
                        )}
                        <div className="channels-selection-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                            {/* WhatsApp Card */}
                            <div
                                className={`channel-select-card ${channels.includes('whatsapp') ? 'selected' : ''}`}
                                onClick={() => {
                                    setErrors({});
                                    if (channels.includes('whatsapp')) {
                                        if (channels.length === 1) {
                                            alert('Please keep at least one delivery channel selected.');
                                            return;
                                        }
                                        setChannels(channels.filter(c => c !== 'whatsapp'));
                                    } else {
                                        setChannels([...channels, 'whatsapp']);
                                    }
                                }}
                            >
                                <span className="channel-icon wa">🟢</span>
                                <div className="channel-info">
                                    <h4>WhatsApp</h4>
                                    <p>Send text cards with action buttons and photo/video attachments.</p>
                                </div>
                                <div className="channel-checkbox-wrapper">
                                    <div className={`channel-checkbox ${channels.includes('whatsapp') ? 'checked' : ''}`}>
                                        {channels.includes('whatsapp') && <Check size={14} strokeWidth={3} />}
                                    </div>
                                </div>
                            </div>

                            {/* Email Card */}
                            <div
                                className={`channel-select-card ${channels.includes('email') ? 'selected' : ''}`}
                                onClick={() => {
                                    setErrors({});
                                    if (channels.includes('email')) {
                                        if (channels.length === 1) {
                                            alert('Please keep at least one delivery channel selected.');
                                            return;
                                        }
                                        setChannels(channels.filter(c => c !== 'email'));
                                    } else {
                                        setChannels([...channels, 'email']);
                                    }
                                }}
                            >
                                <span className="channel-icon mail">📧</span>
                                <div className="channel-info">
                                    <h4>Email</h4>
                                    <p>Deliver gorgeous HTML email invitations to guests.</p>
                                </div>
                                <div className="channel-checkbox-wrapper">
                                    <div className={`channel-checkbox ${channels.includes('email') ? 'checked' : ''}`}>
                                        {channels.includes('email') && <Check size={14} strokeWidth={3} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="dispatch-preview-layout">
                        {/* Smartphone Live Preview Column */}
                        <div className="live-preview-mockup-column">
                            <h3 style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 800, marginBottom: '1.25rem', fontSize: '1.2rem', fontFamily: "'Outfit', sans-serif" }}>🎨 Downloadable WhatsApp Card &amp; Poster</h3>
                            <div style={{ marginBottom: '2.5rem', width: '100%' }}>
                                <InviteCardGenerator 
                                    eventName={eventName}
                                    eventType={eventType}
                                    eventDate={eventDate}
                                    eventVenue={eventVenue}
                                    eventCity={eventCity}
                                    rsvpLink={window.location.origin + "/invite/rsvp/sample-token"}
                                    theme={selectedCardTheme || tone}
                                />
                            </div>

                            <h3 style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>📱 Live WhatsApp Preview</h3>
                            <div className="smartphone-preview-frame">
                                <div className="smartphone-header">
                                    <span className="speaker"></span>
                                </div>
                                <div className="smartphone-screen-body" style={{ padding: '12px' }}>
                                    <div className="preview-chat-bubble">
                                        {mediaUrl && (
                                            <div style={{ marginBottom: '10px', borderRadius: '8px', overflow: 'hidden', border: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }}>
                                                {mediaType === 'video' ? (
                                                    <video
                                                        src={mediaUrl.startsWith('http') ? mediaUrl : `${API_BASE_URL}${mediaUrl}`}
                                                        controls
                                                        style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', display: 'block' }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={mediaUrl.startsWith('http') ? mediaUrl : `${API_BASE_URL}${mediaUrl}`}
                                                        alt="Attached Invite Card"
                                                        style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', display: 'block' }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        <p style={{ whiteSpace: 'pre-wrap' }}>{getPersonalizedPreview()}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Settings & Dispatch Actions Column */}
                        <div className="dispatch-actions-column">
                            <h3 style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>Delivery Settings</h3>

                            {/* 12-Hour Automated Pre-Event Reminder Toggle */}
                            <div style={{
                                background: isLightTheme ? '#FFFFFF' : 'rgba(17, 24, 39, 0.75)',
                                border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(139, 92, 246, 0.3)',
                                borderRadius: '14px',
                                padding: '1.1rem',
                                marginBottom: '1.25rem',
                                boxShadow: isLightTheme ? '0 4px 16px rgba(0, 0, 0, 0.03)' : '0 4px 16px rgba(0, 0, 0, 0.3)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <span style={{ fontSize: '1.3rem' }}>⏰</span>
                                        <div>
                                            <h4 style={{ margin: 0, fontSize: '0.92rem', color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 700 }}>
                                                12-Hour Pre-Event Reminder
                                            </h4>
                                            <p style={{ margin: '3px 0 0 0', fontSize: '0.76rem', color: isLightTheme ? '#64748B' : '#94A3B8', lineHeight: 1.4 }}>
                                                Automatically send a countdown alert 12 hours before the event to all guests who marked <strong>Accepted</strong> or <strong>Tentative</strong>.
                                            </p>
                                        </div>
                                    </div>

                                    <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={reminderEnabled}
                                            onChange={(e) => {
                                                const isEligiblePremium = inviteUser?.isPremium && (inviteUser.maxInvitations || 0) >= 1000;
                                                const totalSent = inviteUser?.totalInvitesSent || 0;
                                                if (!isEligiblePremium) {
                                                    if (totalSent >= 10) {
                                                        if (window.confirm('⏰ 12-Hour Automated Reminders are unlocked on the 1000 (₹699) and 2000 (₹999) plans. Would you like to view packages and upgrade now?')) {
                                                            history.push('/invite/upgrade');
                                                        }
                                                        return;
                                                    }
                                                    if (recipients.length > 10) {
                                                        const proceed = window.confirm(`⏰ 12-Hour Automated Reminders are included for up to 10 guests across all plans. You currently have ${recipients.length} guests. Would you like to upgrade to the 1000 (₹699) or 2000 (₹999) plan? Click OK to upgrade, or Cancel to continue.`);
                                                        if (proceed) {
                                                            history.push('/invite/upgrade');
                                                            return;
                                                        }
                                                    }
                                                }
                                                setReminderEnabled(e.target.checked);
                                            }}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#8B5CF6' }}
                                        />
                                    </label>
                                </div>

                                {!(inviteUser?.isPremium && inviteUser.maxInvitations >= 1000) && (
                                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.72rem', color: '#10B981', fontWeight: 600 }}>
                                            🎁 Included up to 10 guests
                                        </span>
                                        <span 
                                            onClick={() => history.push('/invite/upgrade')}
                                            style={{
                                                background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                                color: '#FFFFFF',
                                                fontSize: '0.72rem',
                                                fontWeight: 700,
                                                padding: '3px 10px',
                                                borderRadius: '16px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            ⭐ Unlocked for 1,000+ with ₹699 &amp; ₹999 Plans
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Advanced Collapse for Alert Preferences */}
                            <button
                                type="button"
                                className="advanced-settings-toggle"
                                onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                            >
                                {isAdvancedOpen ? '▼ Hide Advanced Alert Setup' : '▶ Show Advanced Alert Setup'}
                            </button>

                            {isAdvancedOpen && (
                                <div className="advanced-settings-content" style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.7)', border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '1rem', marginTop: '0.75rem' }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#7C3AED', fontWeight: 700 }}>📊 Complete Delivery Report Preferences</h4>
                                    <p style={{ fontSize: '0.78rem', color: isLightTheme ? '#64748B' : '#94A3B8', margin: '0 0 1rem 0' }}>Receive analytics reports once sending completes.</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer', color: isLightTheme ? '#334155' : '#CBD5E1' }}>
                                            <input type="radio" checked={notificationChannel === 'none'} onChange={() => setNotificationChannel('none')} />
                                            No Report Notification (Free)
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer', color: isLightTheme ? '#334155' : '#CBD5E1' }}>
                                            <input type="radio" checked={notificationChannel === 'email'} onChange={() => setNotificationChannel('email')} />
                                            Email Report (Free)
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer', opacity: inviteUser?.isPremium ? 1 : 0.6, color: isLightTheme ? '#334155' : '#CBD5E1' }}>
                                            <input
                                                type="radio"
                                                checked={notificationChannel === 'whatsapp'}
                                                onChange={() => {
                                                    if (inviteUser?.isPremium) setNotificationChannel('whatsapp');
                                                    else if (window.confirm('WhatsApp complete reports are available for Premium accounts. Upgrade now?')) history.push('/invite/upgrade');
                                                }}
                                            />
                                            WhatsApp Instant Report (Pro ⚡)
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Run Test Delivery */}
                            <div className="test-send-form" style={{ marginTop: '1.5rem', padding: '1rem', border: isLightTheme ? '1.5px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '12px', background: isLightTheme ? '#FFFFFF' : 'rgba(15, 23, 42, 0.6)' }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#7C3AED', fontWeight: 700 }}>Test Invitation Delivery</h4>
                                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                                    <input
                                        type="tel"
                                        placeholder="Test phone (e.g. +91XXXXXXXXXX)"
                                        value={testPhone}
                                        onChange={e => setTestPhone(e.target.value)}
                                        style={{ fontSize: '0.85rem', padding: '0.5rem', background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.8)', border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)', color: isLightTheme ? '#0F172A' : '#F8FAFC', borderRadius: '8px' }}
                                    />
                                </div>
                                <button onClick={handleTestSend} className="run-test-dispatch-btn" disabled={loading} style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}>
                                    {loading ? 'Sending Test...' : 'Send Test Invite ✉️'}
                                </button>
                                {testResult && <div className="test-send-result" style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#10B981' }}>{testResult}</div>}
                            </div>

                            <hr className="divider" style={{ margin: '1.5rem 0', border: 'none', borderTop: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }} />

                            {/* CONDITIONAL AUTH OVERLAY (Anonymous AND Recipients > 10) */}
                            {!inviteUser && recipients.length > 10 ? (
                                <div className="upgrade-warning-box" style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(17, 24, 39, 0.85)', border: '1px dashed rgba(139, 92, 246, 0.4)', padding: '1.5rem', borderRadius: '14px' }}>
                                    <h4 style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontSize: '1rem', marginBottom: '0.5rem', fontWeight: 800 }}>⚠️ Free Tier Limit Reached</h4>
                                    <p style={{ fontSize: '0.85rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginBottom: '1.25rem' }}>
                                        You have added <strong>{recipients.length}</strong> guests. Anonymous invitations are capped at 10 free invites. Please sign up or log in to send invitations.
                                    </p>

                                    <div id="wizard-google-btn" style={{ marginBottom: '1rem', width: '100%' }}></div>

                                    <div style={{ display: 'flex', alignItems: 'center', margin: '0.75rem 0', color: isLightTheme ? '#64748B' : '#64748B' }}>
                                        <hr style={{ flex: 1, border: 'none', borderTop: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }} />
                                        <span style={{ padding: '0 8px', fontSize: '0.75rem' }}>or use email</span>
                                        <hr style={{ flex: 1, border: 'none', borderTop: isLightTheme ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)' }} />
                                    </div>

                                    {authError && <div className="auth-error-badge" style={{ fontSize: '0.8rem', color: '#DC2626' }}>⚠️ {authError}</div>}

                                    <form onSubmit={handleAuthSubmit} className="auth-form" style={{ marginTop: '0.75rem' }}>
                                        {authMode === 'register' && (
                                            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                                                <input type="text" placeholder="Full Name" value={authName} onChange={e => setAuthName(e.target.value)} required style={{ fontSize: '0.85rem', padding: '0.5rem', background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.8)', border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)', color: isLightTheme ? '#0F172A' : '#F8FAFC', borderRadius: '8px' }} />
                                            </div>
                                        )}
                                        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                                            <input type="email" placeholder="Email Address" value={authEmail} onChange={e => setAuthEmail(e.target.value)} required style={{ fontSize: '0.85rem', padding: '0.5rem', background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.8)', border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)', color: isLightTheme ? '#0F172A' : '#F8FAFC', borderRadius: '8px' }} />
                                        </div>
                                        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                                            <input type="password" placeholder="Password" value={authPassword} onChange={e => setAuthPassword(e.target.value)} required style={{ fontSize: '0.85rem', padding: '0.5rem', background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.8)', border: isLightTheme ? '1.5px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.12)', color: isLightTheme ? '#0F172A' : '#F8FAFC', borderRadius: '8px' }} />
                                        </div>

                                        <button type="submit" className="auth-submit-btn" disabled={loading} style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', padding: '0.6rem', fontSize: '0.85rem', color: '#FFF', fontWeight: 700, borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                                            {loading ? 'Processing...' : authMode === 'login' ? 'Log In & Continue' : 'Sign Up & Continue'}
                                        </button>
                                    </form>

                                    <p style={{ fontSize: '0.8rem', textAlign: 'center', marginTop: '1rem', margin: '0.75rem 0 0', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                                        {authMode === 'login' ? (
                                            <>New here? <span onClick={() => { setAuthMode('register'); setAuthEmail(''); setAuthPassword(''); setAuthName(''); setAuthError(null); }} style={{ color: '#7C3AED', fontWeight: 'bold', cursor: 'pointer' }}>Sign Up</span></>
                                        ) : (
                                            <>Already have an account? <span onClick={() => { setAuthMode('login'); setAuthEmail(''); setAuthPassword(''); setAuthName(''); setAuthError(null); }} style={{ color: '#7C3AED', fontWeight: 'bold', cursor: 'pointer' }}>Log In</span></>
                                        )}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <h3 style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC', fontWeight: 800, fontFamily: "'Outfit', sans-serif" }}>Final Dispatch Details</h3>
                                    <div className="final-dispatch-details" style={{ fontSize: '0.85rem', lineHeight: '1.5', color: isLightTheme ? '#334155' : '#CBD5E1' }}>
                                        <p>Host Family: <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>{hostName || 'Host'}</strong></p>
                                        <p>Total Recipients: <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>{recipients.length}</strong></p>
                                        <p>Delivery Channels: <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>{channels.join(', ')}</strong></p>
                                        <p>RSVP Add-ons: <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>{collectDietary ? '🍲 Dietary Preferences' : 'No Dietary'}, {collectAccommodation ? '🏨 Accommodation & Travel' : 'No Accommodation'}</strong></p>
                                        <p>Status: <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>{inviteUser ? 'Authenticated Plan' : 'Free Anonymous Send'}</strong></p>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
                                        {/* Step 3 WhatsApp Warning Banner */}
                                        {channels.includes('whatsapp') && whatsappStatus !== 'connected' && (
                                            <div className="step3-whatsapp-warning-box">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <AlertCircle size={18} color="#F59E0B" />
                                                    <strong>WhatsApp Not Linked</strong>
                                                </div>
                                                <p>To dispatch invitations directly to WhatsApp with photos and RSVP tracking, please link your WhatsApp account.</p>
                                                <button
                                                    type="button"
                                                    onClick={() => handleConnectWhatsApp(3)}
                                                    className="step3-connect-wa-btn"
                                                >
                                                    📲 Connect WhatsApp Now
                                                </button>
                                            </div>
                                        )}

                                        <button onClick={handleFinalSend} className="final-dispatch-all-btn" disabled={loading}>
                                            {loading ? 'Sending...' : '🚀 Send Invitations Now'}
                                        </button>
                                        {inviteUser && (
                                            <button onClick={handleSaveDraft} className="save-draft-btn" disabled={loading}>
                                                {loading ? 'Saving Draft...' : '📁 Save as Draft (Edit Later)'}
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="step-actions">
                        <button onClick={handleSaveDraft} className="wizard-save-draft-btn" disabled={loading}>
                            {loading ? 'Saving...' : '📁 Save Draft'}
                        </button>
                    </div>
                </div>
            )}



            {/* Step 1 WhatsApp Link Prompt Modal */}
            {showWaStep1Modal && (
                <div className="wizard-modal-overlay">
                    <div className="wizard-modal-container" style={{ maxWidth: '500px' }}>
                        <div className="wizard-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '1.4rem' }}>📲</span>
                                <h3 style={{ margin: 0, fontSize: '1.2rem', fontFamily: "'Outfit', sans-serif" }}>Connect WhatsApp First?</h3>
                            </div>
                            <button className="wizard-modal-close-btn" onClick={() => setShowWaStep1Modal(false)}>&times;</button>
                        </div>
                        <div className="wizard-modal-body" style={{ padding: '1.25rem 1.5rem' }}>
                            <div style={{
                                background: isLightTheme ? '#FFFBEB' : 'rgba(245, 158, 11, 0.1)',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                                borderRadius: '12px',
                                padding: '1rem',
                                marginBottom: '1.25rem',
                                display: 'flex',
                                gap: '10px'
                            }}>
                                <span style={{ fontSize: '1.5rem' }}>💡</span>
                                <div style={{ fontSize: '0.86rem', color: isLightTheme ? '#78350F' : '#FCD34D', lineHeight: 1.45 }}>
                                    <strong>Recommended Step:</strong> Connecting your WhatsApp now ensures your event invitations, photo posters, and RSVP tracking are ready to broadcast smoothly without delays at the final step.
                                </div>
                            </div>
                            <p style={{ fontSize: '0.88rem', color: isLightTheme ? '#334155' : '#CBD5E1', lineHeight: 1.5, margin: 0 }}>
                                It takes under 30 seconds to pair your phone or scan a QR code. Your draft will be saved automatically so you won't lose any entered details.
                            </p>
                        </div>
                        <div className="wizard-modal-footer" style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                            <button
                                type="button"
                                className="wa-btn-primary"
                                style={{ width: '100%', padding: '0.85rem' }}
                                onClick={() => {
                                    setShowWaStep1Modal(false);
                                    handleConnectWhatsApp(2);
                                }}
                            >
                                📲 Connect WhatsApp Now (Recommended)
                            </button>
                            <button
                                type="button"
                                className="wa-btn-secondary"
                                style={{ width: '100%', padding: '0.75rem' }}
                                onClick={() => {
                                    setShowWaStep1Modal(false);
                                    setCurrentStep(2);
                                    setErrors({});
                                    setTouched({});
                                }}
                            >
                                Continue to Templates (Connect Later) ➡️
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3 Final Dispatch WhatsApp Required Modal */}
            {showWaDispatchModal && (
                <div className="wizard-modal-overlay">
                    <div className="wizard-modal-container" style={{ maxWidth: '480px' }}>
                        <div className="wizard-modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '1.4rem' }}>📲</span>
                                <h3 style={{ margin: 0, fontSize: '1.2rem', fontFamily: "'Outfit', sans-serif" }}>WhatsApp Connection Required</h3>
                            </div>
                            <button className="wizard-modal-close-btn" onClick={() => setShowWaDispatchModal(false)}>&times;</button>
                        </div>
                        <div className="wizard-modal-body" style={{ padding: '1.25rem 1.5rem' }}>
                            <p style={{ fontSize: '0.9rem', color: isLightTheme ? '#334155' : '#CBD5E1', lineHeight: 1.5, margin: '0 0 1rem 0' }}>
                                Your WhatsApp account is currently not connected. Please link your WhatsApp account via QR code or phone pairing code so the system can broadcast your invitations to your <strong>{recipients.length}</strong> guests.
                            </p>
                            <div style={{
                                background: isLightTheme ? '#ECFDF5' : 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.3)',
                                borderRadius: '12px',
                                padding: '0.85rem',
                                fontSize: '0.82rem',
                                color: isLightTheme ? '#065F46' : '#6EE7B7'
                            }}>
                                🔒 Your invitation campaign draft has been automatically saved. You will return right back here once connected!
                            </div>
                        </div>
                        <div className="wizard-modal-footer" style={{ display: 'flex', gap: '0.75rem' }}>
                            <button
                                type="button"
                                className="dismiss-btn"
                                onClick={() => setShowWaDispatchModal(false)}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="wa-btn-primary"
                                style={{ flex: 1 }}
                                onClick={() => {
                                    setShowWaDispatchModal(false);
                                    handleConnectWhatsApp(3);
                                }}
                            >
                                📲 Connect WhatsApp Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InviteWizard;
